import { useEffect, useRef, useState } from 'react';
import { adresseApi } from '../../api/client';
import { PayeLe } from '../../components/PayeLe.jsx';
import { envoisApi, photosApi, piecesApi, plansApi } from '../../api/resources';
import { reduitLImage } from '../../lib/image';
import {
  documentsDuChantier,
  estUnAchat,
  ListeDuClient,
  useAchatsJoints,
  usePiecesDesDocuments,
} from '../../components/ListeDuClient.jsx';
import { Shell } from '../../components/layout/Shell.jsx';
import { PhotoBtn } from '../../components/PhotoBtn.jsx';
import { PhotosDeLOffre } from './PhotosDeLOffre.jsx';
import { ScanFacture } from '../../components/ScanFacture.jsx';
import { articlesLus } from '../../components/FacturesJointes.jsx';
import { QRCode } from '../../components/QRCode.jsx';
import { SignPad } from '../../components/SignPad.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { FR } from '../../components/ui/FieldRow.jsx';
import { Inp, NumInput, _inp, inpFS } from '../../components/ui/Inputs.jsx';
import { Pill } from '../../components/ui/Pill.jsx';
import { Tabs } from '../../components/ui/Tabs.jsx';
import { ChoixPhotosClient } from './ChoixPhotosClient.jsx';
import { JournalChantier } from './JournalChantier.jsx';
import { PortailClient } from './PortailClient.jsx';
import { RetourAchatDepot } from './RetourAchatDepot.jsx';
import { DOCUMENTS_ANS, PHOTO_ANS } from '../../constants/conservation';
import { metierCats } from '../../constants/trades';
import { adresseAssemblee } from '../../lib/adresse';
import { dateSuisse } from '../../lib/dates';
import { dateVersIso, dtoVersChantierDetail, isoVersDate } from '../../api/mappers';
import { HeuresParMois } from './HeuresParMois.jsx';
import { HeuresPrevues } from './HeuresPrevues.jsx';
import { useEquipes } from '../../api/useEquipes';
import {
  ACCESS_PERMS,
  ACCESS_ROLE,
  roleCan,
  suitLesDepenses,
  voitLeBenefice,
  voitLesPrixDesOffres,
} from '../../lib/access';
import {
  avancementDesPostes,
  avancementDuChantier,
  chantierCosts,
  fraisDuChantier,
  factureAuClient,
} from '../../lib/costs';
import { DEVISE, fmt, fmtH } from '../../lib/currency';
import { champsFacture } from '../../lib/facture-fournisseur';
import { messageTitreManquant, rangsSansTitre } from '../../lib/lignes-document';
import { swissQR } from '../../lib/swiss-qr';
import { aujourdhuiIso, hours, today, uid } from '../../lib/utils';
import { CLE_CHANTIERS_ROUVERTS, termineLeChantier } from '../../lib/chantier-termine';
import { useReglages } from '../../api/reglages.jsx';
import { dateIso } from '../../lib/journal';
import { C } from '../../theme/palette';
import { finDeJournee, JOURNEE_MAX, pauseDuContrat, pauseLegale } from '../../lib/pointage';
import { termesOffre } from '../../lib/terminologie';
import { tr, trf } from '../../lib/i18n';
import { tauxTvaEnVigueur } from '../../lib/tva';
import { chantierDuDevis } from '../../lib/projet';
import { nomComplet } from '../../lib/acces';
import { PhotoAgrandie } from '../../components/PhotoAgrandie.jsx';
import { DocumentsJoints, ouvrePiece } from '../../components/DocumentsJoints.jsx';
import { AffaireChantier } from '../accueil/AffaireChantier.jsx';
import { suiviAffaires } from '../../lib/suivi-affaires';

/** Taille maximale d'un plan, en octets : le serveur applique la même limite. */
const TAILLE_MAX_PLAN = 50 * 1024 * 1024;

/** Les onglets d'un chantier toujours en vue ; les autres sont sous « ⋯ Plus ». */
const ONGLETS_PRINCIPAUX = ['resume', 'journal', 'heures', 'photos', 'messages'];

/**
 * Les coûts qu'un onglet fermé retire de la réponse du serveur : un chiffre
 * calculé sans eux est faux. Le technicien qui voyait le bénéfice sans les
 * factures fournisseurs lisait +259.94 là où le patron lisait −896.40.
 *
 * @returns {{heures: boolean, sous: boolean, facf: boolean}} vrai pour un onglet fermé
 */
function coutsFermes() {
  const cp = ACCESS_PERMS[ACCESS_ROLE];
  return {
    heures: !!cp && cp.chHeures === false,
    sous: !!cp && cp.chSous === false,
    facf: !!cp && cp.chFacf === false,
  };
}

/** Garde les cases dont les coûts sont connus, ôte les autres. */
function casesConnues(cases, fermes) {
  return cases.filter(([l]) => {
    if (fermes.facf && (l === 'Matériaux' || l === 'Matériel')) return false;
    if (fermes.sous && l === 'Sous-trait.') return false;
    if (fermes.heures && (l === 'M.O.' || l === 'Heures')) return false;
    return true;
  });
}

export function ChantiersPage({
  onNav,
  soc,
  chantiers,
  setChantiers,
  clients,
  employes,
  factures,
  setFactures,
  devis,
  setDevis,
  frais,
  rechargeFactures,
  estMixte,
  univers,
  setUnivers,
  ouvrir,
}) {
  // Un chantier rouvert à la main reste ouvert : sa facture finale déjà
  // partie ne le referme plus au chargement suivant.
  const { reglageJson, setReglageJson } = useReglages();
  const noteRouvert = (id, rouvert) => {
    const avant = (reglageJson(CLE_CHANTIERS_ROUVERTS, []) || []).filter(
      (x) => String(x) !== String(id),
    );
    setReglageJson(CLE_CHANTIERS_ROUVERTS, rouvert ? [...avant, id] : avant);
  };
  const [selId, setSelId] = useState(null);
  // L'équipe qui suit le chantier (patron) ou la mienne (chef d'équipe).
  const { equipes } = useEquipes();
  const [voirTermines, setVoirTermines] = useState(false);
  const [tab, setTab] = useState('resume');
  const [tousOnglets, setTousOnglets] = useState(false);
  // La recherche mène au chantier touché, et non à la liste. Une seule fois :
  // la liste rechargée ne doit pas le rouvrir après « ← ».
  const dejaOuvert = useRef(null);
  useEffect(() => {
    if (ouvrir?.chantierId == null || dejaOuvert.current === ouvrir) return;
    if (!(chantiers || []).some((c) => c.id === ouvrir.chantierId)) return;
    dejaOuvert.current = ouvrir;
    setSelId(ouvrir.chantierId);
    setTab('resume');
  }, [ouvrir, chantiers]);
  // La liste du chantier : marchandise, sous-traitance et photos jointes à ses
  // offres et factures, même payées. Elle compte dans ses coûts.
  const chOuvert = selId ? (chantiers || []).find((x) => x.id === selId) : null;
  const docsOuverts = chOuvert
    ? documentsDuChantier(chOuvert, devis, factures)
    : { devis: [], factures: [] };
  const piecesOuvertes = usePiecesDesDocuments(docsOuverts.devis, docsOuverts.factures);
  const achatsJoints = piecesOuvertes.filter((x) => estUnAchat(x.piece)).map((x) => x.piece);
  // Les achats joints aux offres et factures de tous les chantiers : la liste
  // montre les mêmes chiffres que la fiche du chantier.
  const achatsDeLaSociete = useAchatsJoints((factures || []).length + ':' + (devis || []).length);
  const [payQrId, setPayQrId] = useState(null);
  const [chPhotoCat, setChPhotoCat] = useState('avant');
  const [chPhotoPoste, setChPhotoPoste] = useState('');
  const [chPhotoNote, setChPhotoNote] = useState('');
  const [cQ, setCQ] = useState('');
  const [msgForm, setMsgForm] = useState('');
  const [stForm, setStForm] = useState(null);
  const [facForm, setFacForm] = useState(null);
  const [ptForm, setPtForm] = useState(null);
  const [pvForm, setPvForm] = useState(null);
  const [photoFil, setPhotoFil] = useState('tous');
  const [avForm, setAvForm] = useState(null);
  const [avSign, setAvSign] = useState('');
  const upd = (id, fn) => setChantiers((p) => p.map((c) => (c.id === id ? fn(c) : c)));

  // Les photos ne suivent pas le chantier : elles s'envoient une par une, avec
  // leur fichier, et se retirent de meme. Le reste du chantier continue de
  // s'enregistrer par la synchronisation ordinaire.
  const [photoErreur, setPhotoErreur] = useState('');

  const deposePhoto = async (chantierId, fichier, description) => {
    setPhotoErreur('');
    try {
      const photo = await photosApi.depose(chantierId, fichier, description);
      upd(chantierId, (c) => ({
        ...c,
        photos: [...(c.photos || []), { ...description, id: photo.id, src: photo.image }],
      }));
    } catch (e) {
      setPhotoErreur(e.message || "La photo n'a pas pu être envoyée.");
    }
  };

  // Un plan est un fichier, comme une photo : il s'envoie a son propre point
  // d'entree. Ecrit dans l'agregat, il ne quittait pas le navigateur — la ligne
  // survivait au rechargement, le plan non.
  const deposePlan = async (chantierId, fichier, description) => {
    setPhotoErreur('');
    // Le PDF d'un architecte pèse souvent 15 à 40 Mo ; le serveur en admet 50.
    if (fichier && fichier.size > TAILLE_MAX_PLAN) {
      setPhotoErreur(
        trf(
          'Ce plan pèse {0} Mo, au-delà des 50 Mo admis. Demande à l’architecte une version allégée.',
          (fichier.size / (1024 * 1024)).toFixed(1),
        ),
      );
      return;
    }
    try {
      const plan = await plansApi.depose(chantierId, fichier, description);
      upd(chantierId, (c) => ({
        ...c,
        plans: [
          ...(c.plans || []),
          {
            id: plan.id,
            nom: plan.nom,
            emoji: plan.emoji || '📐',
            type: plan.typeFichier || '',
            version: plan.version || 1,
            date: today(),
            taille: plan.taille || '',
            envoye: false,
            src: plan.fichier ? adresseApi(plan.fichier) : undefined,
          },
        ],
      }));
    } catch (e) {
      setPhotoErreur(e.message || "Le plan n'a pas pu être envoyé.");
    }
  };

  // Le scan d'une facture fournisseur ou du contrat d'un sous-traitant est une
  // piece jointe du chantier : la ligne n'en garde que la reference. Ecrit en
  // base64 dans la ligne, il ne quittait pas le navigateur et la vignette
  // disparaissait au rechargement. Sans fichier (ou chantier pas encore
  // enregistre), la ligne part sans scan.
  const deposePiece = async (chantierId, fichier, libelle) => {
    if (!fichier || typeof chantierId !== 'number') return null;
    try {
      const piece = await piecesApi.depose('CHANTIER', chantierId, fichier, libelle);
      return { pieceId: piece.id, photo: piece.fichier ? adresseApi(piece.fichier) : '' };
    } catch (e) {
      setPhotoErreur(e.message || "Le scan n'a pas pu être enregistré ; la ligne l'est sans lui.");
      return null;
    }
  };

  const retirePhoto = async (chantierId, photoId) => {
    setPhotoErreur('');
    try {
      await photosApi.supprime(chantierId, photoId);
      upd(chantierId, (c) => ({ ...c, photos: (c.photos || []).filter((x) => x.id !== photoId) }));
    } catch (e) {
      setPhotoErreur(e.message || "La photo n'a pas pu être retirée.");
    }
  };

  // La position refusée ou introuvable : le bouton ne faisait rien, sans un mot.
  const positionRefusee = () =>
    window.alert(
      tr(
        'Position introuvable : autorise la localisation pour SwissTech dans les réglages du téléphone, puis réessaie.',
      ),
    );

  if (selId) {
    const ch = chantiers.find((x) => x.id === selId);
    if (!ch) return null;
    const cl = clients.find((c) => c.id === ch.clientId);
    const { fac, st, mo, mat, ben, tauxTva, tvaFacturee, tvaRecuperable } = chantierCosts(
      ch,
      employes,
      soc,
      factures,
      frais,
      achatsJoints,
    );
    // Le montant du chantier est celui de l'offre ; ce qui est parti chez le
    // client se lit sur ses factures.
    const dejaFacture = factureAuClient(ch, factures);
    const resteAFacturer = Math.max(0, (+ch.facture || 0) - dejaFacture.ht);
    const libelleMontant = ch.devisId ? 'Offre HT' : 'Montant HT';
    const affaireDuChantier = ch.devisId
      ? suiviAffaires({ devis, factures, chantiers }).affaires.find((a) => a.id === ch.devisId)
      : null;
    const travauxTtc =
      affaireDuChantier?.total ??
      Math.round((+ch.facture || 0) * (1 + (+tauxTva || 0) / 100) * 100) / 100;
    const retenueTtc = Math.round(travauxTtc * (+ch.pv?.retenue || 0)) / 100;
    // La TVA prévue par l'offre (avenants compris) : celle que le chantier
    // facturera en tout, à côté de celle déjà facturée.
    const tvaOffre =
      Math.round(
        (affaireDuChantier
          ? affaireDuChantier.total - affaireDuChantier.totalHt
          : (+ch.facture || 0) * ((+tauxTva || 0) / 100)) * 100,
      ) / 100;
    // La TVA que le chantier versera à l'État : celle de l'offre (ou celle
    // déjà facturée, si elle la dépasse), moins celle payée sur les achats.
    // Avant la première facture, elle n'était pas à 0.00.
    const tvaDue =
      Math.round((Math.max(tvaOffre, +tvaFacturee || 0) - (+tvaRecuperable || 0)) * 100) / 100;
    const marge = ch.facture > 0 ? Math.round((ben / ch.facture) * 100) : 0;
    const fermes = coutsFermes();
    // Un coût caché à ce rôle fausse le bénéfice : il ne s'affiche pas.
    const voitBenef = voitLeBenefice() && !fermes.heures && !fermes.sous && !fermes.facf;
    // La secrétaire suit le chantier : le facturé, ce qui est payé, les
    // dépenses. Ni la main-d'œuvre, ni la marge, ni le bénéfice.
    const suitLeChantier = suitLesDepenses();
    // « Offres sans les prix » : ni le montant de l'offre, ni la facturation.
    const prixOk = voitLesPrixDesOffres();
    const facturationOk = prixOk && roleCan('factures');
    const _cp = ACCESS_PERMS[ACCESS_ROLE];
    const _chTab = {
      heures: 'chHeures',
      journal: 'chHeures',
      sous: 'chSous',
      facf: 'chFacf',
      // « Factures fournisseurs / matériel » : le matériel du dépôt aussi.
      materiel: 'chFacf',
      photos: 'chPhotos',
      plans: 'chPhotos',
    };
    const msgNonLus = (ch.messages || []).filter((m) => m.de === 'ouvrier' && !m.lu).length;
    const TABS = [
      ['resume', '📊 Résumé'],
      ['journal', '📒 Journal'],
      ['portail', '🌐 Portail'],
      ['heures', '⏱ Heures'],
      ['sous', '🤝 ' + tr('Sous-traitants')],
      ['facf', '📥 Factures four.'],
      ['materiel', '📦 Matériel'],
      ['avenants', '➕ Avenants'],
      ['reception', '✅ Réception'],
      ['plans', '📐 Plans'],
      ['photos', '📷 Photos'],
      ['messages', trf('💬 Messages{0}', msgNonLus ? ' (' + msgNonLus + ')' : '')],
    ].filter(([_k]) => !_cp || !_chTab[_k] || _cp[_chTab[_k]] !== false);
    // Cinq onglets en vue, le reste sous « ⋯ Plus » : douze onglets sur trois
    // rangées se lisaient mal. L'onglet ouvert reste toujours visible.
    const ongletsVisibles =
      tousOnglets || !ONGLETS_PRINCIPAUX.includes(tab)
        ? [...TABS, ...(tousOnglets ? [['__moins', '▴ ' + tr('Masquer')]] : [])]
        : [...TABS.filter(([k]) => ONGLETS_PRINCIPAUX.includes(k)), ['__plus', '⋯ Plus']];
    return (
      <Shell
        active="chantiers"
        onNav={onNav}
        soc={soc}
        back
        onBack={() => {
          setSelId(null);
          setTab('resume');
        }}
        estMixte={estMixte}
        univers={univers}
        setUnivers={setUnivers}
      >
        <div
          style={{
            background: 'linear-gradient(135deg,' + C.navy + ',' + C.navyM + ')',
            borderRadius: 14,
            padding: 14,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 10,
            }}
          >
            <div style={{ flex: 1, marginRight: 10 }}>
              <div
                style={{ fontSize: 9, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase' }}
              >
                {ch.code || ch.id}
              </div>
              <div style={{ fontSize: 15, fontWeight: 900, color: '#fff', lineHeight: 1.3 }}>
                {ch.nom}
              </div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,.5)' }}>{cl?.nom}</div>
              {/* Une epingle qui ne designe rien : le chantier d'un client
                  note a la hate affichait « 📍 · ». */}
              {adresseAssemblee(ch.adr, ch.npa) && (
                <div style={{ fontSize: 8.5, color: 'rgba(255,255,255,.4)' }}>
                  📍{' '}
                  {[ch.adr, ch.npa]
                    .map((m) => String(m ?? '').trim())
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
              {(() => {
                const q = (equipes || []).find((x) => (x.chantierIds || []).includes(ch.id));
                return q ? (
                  <div
                    data-equipe-chantier
                    style={{ fontSize: 9.5, color: '#fff', fontWeight: 700, marginTop: 3 }}
                  >
                    👥 {q.nom}
                    {q.chefNom ? ' · ' + trf('chef : {0}', q.chefNom) : ''}
                  </div>
                ) : null;
              })()}
              {/* Le bon de commande de la régie ou de la société : il dit de
                  quel chantier il s'agit, et la facture qui le porte est payée. */}
              {(() => {
                const pieces = documentsDuChantier(ch, devis, factures);
                const bc = [
                  ...new Set(
                    [...pieces.devis, ...pieces.factures]
                      .map((d) => String(d?.commandeClient ?? '').trim())
                      .filter(Boolean),
                  ),
                ];
                return bc.length > 0 ? (
                  <div
                    data-bon-commande-chantier
                    style={{ fontSize: 9.5, color: '#fff', fontWeight: 700, marginTop: 3 }}
                  >
                    🧾 {trf('Bon de commande : {0}', bc.join(', '))}
                  </div>
                ) : null;
              })()}
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}
            >
              <Pill
                label={ch.statut}
                color={ch.statut === 'Terminé' ? C.green : C.amber}
                size={8}
              />
              {/* Le chantier fini se termine d'un toucher ; seul le PV de
                  réception le faisait. Terminé par erreur, il se rouvre. */}
              <button
                type="button"
                onClick={() => {
                  noteRouvert(selId, ch.statut === 'Terminé');
                  upd(selId, (c) =>
                    c.statut === 'Terminé'
                      ? { ...c, statut: 'En cours' }
                      : termineLeChantier(c, aujourdhuiIso()),
                  );
                }}
                style={{
                  background: 'rgba(255,255,255,.14)',
                  border: '1px solid rgba(255,255,255,.35)',
                  borderRadius: 8,
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 800,
                  padding: '4px 9px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {ch.statut === 'Terminé' ? tr('Rouvrir') : tr('✓ Terminé')}
              </button>
            </div>
          </div>
          <div
            style={{
              background: 'rgba(255,255,255,.1)',
              borderRadius: 99,
              height: 6,
              marginBottom: 4,
            }}
          >
            <div
              style={{
                width: avancementDuChantier(ch) + '%',
                height: 6,
                borderRadius: 99,
                background: '#fff',
              }}
            />
          </div>
          <div style={{ fontSize: 8, color: 'rgba(255,255,255,.5)', marginBottom: 10 }}>
            Avancement {avancementDuChantier(ch)}%
          </div>
          {/* Le chiffre d'affaires et les coûts du chantier : la direction voit
              tout ; la secrétaire voit le montant, les dépenses et le facturé,
              sans la main-d'œuvre ni la marge ; le technicien rien — sauf
              droit « bénéfice » donné par le patron. */}
          {suitLeChantier && (
            <div
              data-testid="chiffres-chantier"
              style={{
                display: 'grid',
                // Une ligne, autant de colonnes que de cases montrées.
                gridAutoFlow: 'column',
                gridAutoColumns: '1fr',
                gap: 5,
              }}
            >
              {casesConnues(
                voitBenef
                  ? [
                      [libelleMontant, fmt(ch.facture)],
                      // Factures des fournisseurs et matériel pris au dépôt : tout ce que
                      // la matière a coûté au chantier.
                      ['Matériaux', fmt(fac + mat)],
                      ['Sous-trait.', fmt(st)],
                      ['M.O.', fmt(mo)],
                      // La marge à côté des coûts, d'un coup d'œil.
                      ['Marge', marge + ' %', ben >= 0 ? '#7CE0A4' : '#FFA3A3'],
                    ]
                  : [
                      ...(prixOk ? [[libelleMontant, fmt(ch.facture)]] : []),
                      ...(facturationOk ? [['Facturé HT', fmt(dejaFacture.ht), '#7CE0A4']] : []),
                      ['Matériaux', fmt(fac + mat)],
                      ['Sous-trait.', fmt(st)],
                    ],
                fermes,
              ).map(([l, v, couleur]) => (
                <div
                  key={l}
                  style={{
                    background: 'rgba(255,255,255,.08)',
                    borderRadius: 8,
                    padding: '7px 4px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 6.5, color: 'rgba(255,255,255,.5)' }}>{l}</div>
                  <div style={{ fontSize: 8.5, fontWeight: 800, color: couleur || '#fff' }}>
                    {v}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* La TVA du chantier, d'un coup d'œil : celle facturée au client,
              celle payée sur les achats et récupérée, et ce qu'il reste à
              verser à l'État pour ce chantier. */}
          {voitBenef && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5,1fr)',
                gap: 5,
                marginTop: 5,
              }}
            >
              {[
                ['Facturé HT', fmt(dejaFacture.ht), '#7CE0A4'],
                [affaireDuChantier ? 'TVA de l’offre' : 'TVA prévue', fmt(tvaOffre)],
                ['TVA facturée', fmt(tvaFacturee)],
                ['TVA récupérable', fmt(tvaRecuperable)],
                tvaDue >= 0
                  ? ['TVA à payer', fmt(tvaDue), '#FFD27A']
                  : ['TVA à récupérer', fmt(-tvaDue), '#7CE0A4'],
              ].map(([l, v, couleur]) => (
                <div
                  key={l}
                  style={{
                    background: 'rgba(255,255,255,.08)',
                    borderRadius: 8,
                    padding: '7px 4px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 6.5, color: 'rgba(255,255,255,.5)' }}>{l}</div>
                  <div style={{ fontSize: 8.5, fontWeight: 800, color: couleur || '#fff' }}>
                    {v}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ marginBottom: 12 }}>
          <Tabs
            items={ongletsVisibles}
            active={tab}
            onChange={(k) =>
              k === '__plus'
                ? setTousOnglets(true)
                : k === '__moins'
                  ? setTousOnglets(false)
                  : setTab(k)
            }
          />
        </div>

        {tab === 'resume' && (
          <div>
            {voitBenef && (
              <Card>
                <Bar icon="💰" title="Décomposition financière" />
                <div style={{ padding: 13 }}>
                  <FR
                    label={ch.devisId ? 'Montant de l’offre (HT)' : 'Montant du chantier (HT)'}
                    value={DEVISE + ' ' + fmt(ch.facture)}
                    color={C.green}
                  />
                  <FR
                    label="Matériaux — factures fournisseurs (HT)"
                    value={'− ' + DEVISE + ' ' + fmt(fac)}
                  />
                  <FR label="Matériel pris au dépôt" value={'− ' + DEVISE + ' ' + fmt(mat)} />
                  <FR label="Sous-traitants" value={'− ' + DEVISE + ' ' + fmt(st)} />
                  <FR label="Main d'œuvre" value={'− ' + DEVISE + ' ' + fmt(mo)} />
                  <FR
                    label="Bénéfice net"
                    value={DEVISE + ' ' + fmt(ben) + ' (' + marge + '%)'}
                    color={ben >= 0 ? C.green : C.red}
                    bold
                    last
                  />
                </div>
              </Card>
            )}
            {/* Le chantier d'une offre acceptée suit sa facturation comme
                l'accueil : acomptes, avenants, facture finale, d'un toucher. */}
            {suitLeChantier && facturationOk && affaireDuChantier && (
              <Card style={{ marginTop: 11 }}>
                <Bar icon="📤" title="Facturation au client" />
                <div style={{ padding: '6px 11px 4px' }}>
                  <AffaireChantier
                    a={affaireDuChantier}
                    titre={ch.nom || cl?.nom || ''}
                    onNav={onNav}
                    rechargeFactures={rechargeFactures}
                    setFactures={setFactures}
                  />
                </div>
              </Card>
            )}
            {suitLeChantier && facturationOk && !affaireDuChantier && (
              <Card style={{ marginTop: 11 }}>
                <Bar icon="📤" title="Facturation au client" />
                <div style={{ padding: 13 }}>
                  <FR
                    label={ch.devisId ? 'Montant de l’offre (HT)' : 'Montant du chantier (HT)'}
                    value={DEVISE + ' ' + fmt(ch.facture)}
                  />
                  <FR
                    label={trf(
                      'Déjà facturé — {0} facture(s), acomptes compris (HT)',
                      dejaFacture.n,
                    )}
                    value={DEVISE + ' ' + fmt(dejaFacture.ht)}
                    color={C.green}
                  />
                  <FR
                    label="Reste à facturer (HT)"
                    value={DEVISE + ' ' + fmt(resteAFacturer)}
                    color={resteAFacturer > 0 ? C.amber : C.green}
                    bold
                    last
                  />
                </div>
              </Card>
            )}
            {voitBenef && (
              <Card style={{ marginTop: 11 }}>
                <Bar icon="🧾" title="TVA du chantier" />
                <div style={{ padding: 13 }}>
                  <FR
                    label={
                      affaireDuChantier
                        ? 'TVA de l’offre, avenants compris'
                        : trf('TVA prévue ({0} %)', tauxTva)
                    }
                    value={DEVISE + ' ' + fmt(tvaOffre)}
                  />
                  <FR
                    label={trf('TVA des factures envoyées au client ({0} %)', tauxTva)}
                    value={DEVISE + ' ' + fmt(tvaFacturee)}
                  />
                  <FR
                    label="TVA payée sur les achats — récupérable"
                    value={'− ' + DEVISE + ' ' + fmt(tvaRecuperable)}
                  />
                  <FR
                    label={tvaDue >= 0 ? 'TVA à verser à l’État' : 'TVA à récupérer de l’État'}
                    value={DEVISE + ' ' + fmt(Math.abs(tvaDue))}
                    color={tvaDue >= 0 ? C.amber : C.green}
                    bold
                    last
                  />
                  <div style={{ fontSize: 9, color: C.grey, marginTop: 8, lineHeight: 1.45 }}>
                    Les montants du chantier sont comptés hors TVA : la TVA payée sur les factures
                    des fournisseurs se récupère, elle n’est pas un coût. Elle est lue sur chaque
                    facture, ou calculée au taux normal quand elle n’est pas saisie.
                  </div>
                </div>
              </Card>
            )}
            <Card style={{ marginTop: 11 }}>
              <Bar
                icon="📍"
                title="Position GPS du chantier"
                color={C.teal}
                sub="Sert à vérifier la présence des employés sur place"
              />
              <div style={{ padding: 13 }}>
                {ch.gps && typeof ch.gps.lat === 'number' ? (
                  <div>
                    <a
                      href={'https://maps.google.com/?q=' + ch.gps.lat + ',' + ch.gps.lng}
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          background: C.tealL,
                          borderRadius: 8,
                          padding: '9px 11px',
                          marginBottom: 8,
                        }}
                      >
                        <span style={{ fontSize: 9.5, color: C.teal, fontWeight: 700 }}>
                          🗺 {ch.gps.lat.toFixed(4)}, {ch.gps.lng.toFixed(4)}
                        </span>
                        <span style={{ fontSize: 12, color: C.teal }}>›</span>
                      </div>
                    </a>
                    <Btn
                      label="🔄 Redéfinir avec ma position actuelle"
                      sm
                      ghost
                      color={C.navy}
                      full
                      onClick={() => {
                        if (typeof navigator !== 'undefined' && navigator.geolocation) {
                          navigator.geolocation.getCurrentPosition(
                            (po) =>
                              upd(selId, (c) => ({
                                ...c,
                                gps: { lat: po.coords.latitude, lng: po.coords.longitude },
                              })),
                            positionRefusee,
                          );
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 9, color: C.muted, marginBottom: 9, lineHeight: 1.5 }}>
                      Position non définie. Enregistre-la depuis le chantier (sur place) pour
                      pouvoir contrôler que les ouvriers y sont bien.
                    </div>
                    <Btn
                      label="📍 Enregistrer avec ma position actuelle"
                      sm
                      color={C.teal}
                      full
                      onClick={() => {
                        if (typeof navigator !== 'undefined' && navigator.geolocation) {
                          navigator.geolocation.getCurrentPosition(
                            (po) =>
                              upd(selId, (c) => ({
                                ...c,
                                gps: { lat: po.coords.latitude, lng: po.coords.longitude },
                              })),
                            positionRefusee,
                          );
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </Card>
            <Card style={{ marginTop: 11 }}>
              <Bar
                icon="📈"
                title="Avancement du chantier"
                color={C.navy}
                sub="Suit les postes de l’offre cochés par l’équipe ; réglable à la main ; la réception le porte à 100 %"
              />
              <div style={{ padding: 13 }}>
                {/* Le réglage vivait dans l'onglet Portail, aujourd'hui caché :
                    plus aucun écran ne permettait de régler l'avancement. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    aria-label="Avancement du chantier"
                    value={avancementDuChantier(ch)}
                    onChange={(e) =>
                      upd(selId, (c) => ({
                        ...c,
                        av: Math.max(0, Math.min(100, +e.target.value || 0)),
                      }))
                    }
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number"
                    min="0"
                    max="100"
                    aria-label="Avancement en pour cent"
                    value={avancementDuChantier(ch)}
                    onChange={(e) =>
                      upd(selId, (c) => ({
                        ...c,
                        av: Math.max(0, Math.min(100, +e.target.value || 0)),
                      }))
                    }
                    style={{
                      width: 56,
                      border: '1px solid ' + C.border,
                      borderRadius: 7,
                      padding: '6px',
                      fontSize: 11,
                      textAlign: 'center',
                      fontWeight: 800,
                    }}
                  />
                  <span style={{ fontSize: 11, fontWeight: 800, color: C.navy }}>%</span>
                </div>
                <div style={{ height: 7, background: C.navyXL, borderRadius: 99, marginTop: 10 }}>
                  <div
                    style={{
                      width: avancementDuChantier(ch) + '%',
                      height: 7,
                      background: C.teal,
                      borderRadius: 99,
                    }}
                  />
                </div>
                {/* Les postes de l'offre, cochés par l'équipe dans ses rapports :
                    l'avancement se lisait sans savoir ce qui était fait. */}
                {(() => {
                  const { faits, total } = avancementDesPostes(ch);
                  if (!total) return null;
                  const premier = (poste) =>
                    [...(ch.rapports || [])]
                      .filter((r) =>
                        String(r.contenu || '')
                          .split('\n')
                          .some((l) => l.trim() === '✔ ' + String(poste).trim()),
                      )
                      .sort((a, b) => String(a.date).localeCompare(String(b.date)))[0];
                  return (
                    <div data-testid="postes-realises" style={{ marginTop: 12 }}>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: C.muted,
                          textTransform: 'uppercase',
                          marginBottom: 6,
                        }}
                      >
                        {trf('Postes de l’offre réalisés : {0} sur {1}', faits.length, total)}
                      </div>
                      {ch.postes.map((poste) => {
                        const r = faits.includes(poste) ? premier(poste) : null;
                        return (
                          <div
                            key={poste}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 8,
                              fontSize: 11,
                              padding: '5px 0',
                              borderBottom: '1px solid ' + C.border,
                            }}
                          >
                            <span style={{ fontWeight: 700, color: r ? C.green : C.muted }}>
                              {r ? '✔ ' : '○ '}
                              {poste}
                            </span>
                            <span style={{ color: C.muted, fontSize: 10, whiteSpace: 'nowrap' }}>
                              {r ? dateSuisse(r.date) + (r.empNom ? ' · ' + r.empNom : '') : ''}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </Card>
            <Card style={{ marginTop: 11 }}>
              <div style={{ padding: 13 }}>
                <Btn
                  label="🗑 Supprimer ce chantier"
                  ghost
                  color={C.red}
                  full
                  onClick={() => {
                    // Tout ce qui appartient au chantier part avec lui : heures,
                    // rapports, photos, plans, factures fournisseurs, matériel
                    // (le serveur les efface en même temps). L'offre acceptée
                    // qui l'a créé et ses factures pas encore envoyées aussi :
                    // sans quoi l'affaire restait dans l'accueil. Une facture
                    // envoyée est annulée — elle reste au classeur, la loi
                    // demande de la garder — et une facture payée reste.
                    const offre = (devis || []).find(
                      (d) =>
                        (ch.devisId != null && d.id === ch.devisId) ||
                        chantierDuDevis(d, chantiers)?.id === ch.id,
                    );
                    const sesFactures = offre
                      ? (factures || []).filter((f) => f.devisId === offre.id)
                      : [];
                    const brouillons = sesFactures.filter((f) => f.statut === 'Brouillon');
                    const aAnnuler = sesFactures.filter(
                      (f) =>
                        f.statut === 'Envoyée' ||
                        f.statut === 'Partielle' ||
                        f.statut === 'En retard',
                    );
                    const payees = sesFactures.filter((f) => f.statut === 'Payée');
                    const heures = (ch.pointages || []).filter((p) => p.type !== 'absence').length;
                    const lignes = [
                      trf('Supprimer le chantier « {0} » ?', ch.nom),
                      '',
                      tr('Sera supprimé définitivement :'),
                      trf('• {0} pointage(s) d’heures', heures),
                      trf('• {0} rapport(s) du jour', (ch.rapports || []).length),
                      trf(
                        '• {0} photo(s) et plan(s)',
                        (ch.photos || []).length + (ch.plans || []).length,
                      ),
                      trf('• {0} facture(s) fournisseur', (ch.factures || []).length),
                      offre ? trf('• l’offre n° {0}', offre.num || offre.id) : '',
                      brouillons.length
                        ? trf('• {0} facture(s) pas encore envoyée(s)', brouillons.length)
                        : '',
                      aAnnuler.length
                        ? trf(
                            '{0} facture(s) déjà envoyée(s) au client seront annulées (gardées au classeur, comme la loi le demande).',
                            aAnnuler.length,
                          )
                        : '',
                      payees.length
                        ? trf(
                            '{0} facture(s) déjà payée(s) restent : l’argent a été reçu.',
                            payees.length,
                          )
                        : '',
                    ].filter((l, i) => l !== '' || i === 1);
                    if (!window.confirm(lignes.join('\n'))) return;
                    setChantiers && setChantiers((p) => p.filter((x) => x.id !== selId));
                    if (setFactures && sesFactures.length) {
                      const effacees = new Set(brouillons.map((f) => f.id));
                      const annulees = new Set(aAnnuler.map((f) => f.id));
                      setFactures((p) =>
                        p
                          .filter((f) => !effacees.has(f.id))
                          .map((f) =>
                            // Annulée, et détachée de l'offre et du chantier qui
                            // disparaissent : le serveur refusait la mise à jour
                            // d'une facture qui désigne une offre effacée.
                            annulees.has(f.id)
                              ? { ...f, statut: 'Annulée', devisId: null, siteId: null }
                              : f,
                          ),
                      );
                      // Les liens retombés à vide côté serveur (factures payées
                      // gardées) reviennent à l'écran.
                      if (rechargeFactures) setTimeout(() => rechargeFactures(), 2500);
                    }
                    if (offre && setDevis) setDevis((p) => p.filter((d) => d.id !== offre.id));
                    setSelId(null);
                    setTab('resume');
                  }}
                />
              </div>
            </Card>
          </div>
        )}

        {tab === 'portail' && (
          <PortailClient chantier={ch} client={cl}>
            {(() => {
              return (
                <div style={{ marginTop: 13 }}>
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 800,
                      color: C.muted,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      marginBottom: 7,
                    }}
                  >
                    📷 Avancement et photos montrées au client
                  </div>
                  <div
                    style={{
                      background: 'linear-gradient(135deg,' + C.teal + ',#0A5C6E)',
                      borderRadius: 10,
                      padding: '11px 13px',
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span style={{ fontSize: 20, fontWeight: 900, color: '#fff' }}>
                        {avancementDuChantier(ch)}%
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 8, color: 'rgba(255,255,255,.7)' }}>régler</span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={avancementDuChantier(ch)}
                          onChange={(e) =>
                            upd(selId, (c) => ({
                              ...c,
                              av: Math.max(0, Math.min(100, +e.target.value || 0)),
                            }))
                          }
                          style={{
                            width: 52,
                            border: 'none',
                            borderRadius: 7,
                            padding: '5px',
                            fontSize: 11,
                            textAlign: 'center',
                            fontWeight: 800,
                          }}
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        height: 7,
                        background: 'rgba(255,255,255,.25)',
                        borderRadius: 99,
                        marginTop: 8,
                      }}
                    >
                      <div
                        style={{
                          width: avancementDuChantier(ch) + '%',
                          height: 7,
                          background: '#fff',
                          borderRadius: 99,
                        }}
                      />
                    </div>
                  </div>
                  <ChoixPhotosClient
                    photos={ch.photos}
                    onBascule={(id) =>
                      upd(selId, (c) => ({
                        ...c,
                        photos: c.photos.map((x) =>
                          x.id === id ? { ...x, partageClient: !x.partageClient } : x,
                        ),
                      }))
                    }
                  />
                </div>
              );
            })()}
          </PortailClient>
        )}

        {tab === 'heures' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <Btn
                label={ptForm ? 'Fermer' : '+ Pointage'}
                sm
                color={C.amber}
                onClick={() =>
                  setPtForm(
                    ptForm
                      ? null
                      : {
                          empId: employes[0]?.id || 1,
                          date: today(),
                          entree: employes[0]?.heureDebut || '07:00',
                          // La journée du contrat (8 h, 8 h 30 ou 9 h au plus)
                          // et l'heure de midi : 45 minutes d'office faisaient
                          // des journées de 9 h 15.
                          sortie: finDeJournee('07:00', employes[0]?.heuresJour),
                          pause: pauseLegale(
                            '07:00',
                            finDeJournee('07:00', employes[0]?.heuresJour),
                          ),
                          type: 'normal',
                          tache: '',
                        },
                  )
                }
              />
            </div>
            {ptForm && (
              <Card style={{ marginBottom: 11, border: '2px solid ' + C.amber }}>
                <Bar
                  icon="⏱"
                  title={ptForm.editId != null ? 'Corriger le pointage' : 'Nouveau pointage'}
                  sub={ptForm.editId != null ? dateSuisse(ptForm.date) : undefined}
                  color={C.amber}
                />
                <div style={{ padding: 13 }}>
                  {/* Le jour travaillé : on saisit aussi les heures d'un jour
                      passé (février, la semaine dernière…), pas seulement
                      celles d'aujourd'hui. */}
                  <Inp
                    label="Date"
                    type="date"
                    value={dateVersIso(ptForm.date) || ''}
                    onChange={(e) =>
                      e.target.value &&
                      setPtForm((p) => ({ ...p, date: isoVersDate(e.target.value) }))
                    }
                  />
                  <Inp label="Employé">
                    <select
                      value={ptForm.empId}
                      onChange={(e) => {
                        const id = +e.target.value;
                        const choisi = employes.find((x) => x.id === id);
                        setPtForm((p) => {
                          const entree = choisi?.heureDebut || p.entree;
                          const sortie = finDeJournee(entree, choisi?.heuresJour, choisi);
                          return {
                            ...p,
                            empId: id,
                            entree,
                            sortie,
                            pause: pauseLegale(entree, sortie, choisi),
                          };
                        });
                      }}
                      style={{
                        width: '100%',
                        border: '1px solid ' + C.border,
                        borderRadius: 9,
                        padding: '10px',
                        fontSize: 11,
                      }}
                    >
                      {employes.map((e) => (
                        <option key={e.id} value={e.id}>
                          {nomComplet(e) || e.nom}
                        </option>
                      ))}
                    </select>
                  </Inp>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Inp
                      label="Entrée"
                      type="time"
                      value={ptForm.entree}
                      // La pause suit les heures : l'heure de midi (12 h – 13 h)
                      // dès que la journée la traverse.
                      onChange={(e) =>
                        setPtForm((p) => ({
                          ...p,
                          entree: e.target.value,
                          pause: p.pauseManuelle
                            ? p.pause
                            : pauseLegale(
                                e.target.value,
                                p.sortie,
                                employes.find((x) => x.id === p.empId),
                              ),
                        }))
                      }
                    />
                    <Inp
                      label="Sortie"
                      type="time"
                      value={ptForm.sortie}
                      onChange={(e) =>
                        setPtForm((p) => ({
                          ...p,
                          sortie: e.target.value,
                          pause: p.pauseManuelle
                            ? p.pause
                            : pauseLegale(
                                p.entree,
                                e.target.value,
                                employes.find((x) => x.id === p.empId),
                              ),
                        }))
                      }
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Inp
                      label="Pause (min)"
                      type="number"
                      value={ptForm.pause}
                      onChange={(e) =>
                        setPtForm((p) => ({ ...p, pause: +e.target.value, pauseManuelle: true }))
                      }
                    />
                    <Inp label="Type de temps">
                      <select
                        value={ptForm.type || 'normal'}
                        onChange={(e) => setPtForm((p) => ({ ...p, type: e.target.value }))}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 9,
                          padding: '10px',
                          fontSize: 11,
                          background: '#fff',
                        }}
                      >
                        <option value="normal">Heures normales</option>
                        <option value="supp">Heures supplémentaires</option>
                        <option value="deplacement">Déplacement</option>
                        <option value="vacances">Vacances</option>
                        <option value="maladie">Maladie</option>
                        <option value="accident">Accident</option>
                        <option value="formation">Formation</option>
                        <option value="autre">Autre absence</option>
                      </select>
                    </Inp>
                    <Inp label="Où">
                      <select
                        aria-label={tr('Où')}
                        value={ptForm.lieu || ''}
                        onChange={(e) => setPtForm((p) => ({ ...p, lieu: e.target.value }))}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          border: '1px solid ' + C.border,
                          borderRadius: 9,
                          padding: '10px',
                          fontSize: 11,
                          background: '#fff',
                        }}
                      >
                        <option value="">{tr('🏗 Sur le chantier')}</option>
                        <option value="ATELIER">{tr('🏭 À l’atelier, pour ce chantier')}</option>
                      </select>
                    </Inp>
                  </div>
                  {(() => {
                    const effectif = hours(ptForm.entree, ptForm.sortie, +ptForm.pause || 0);
                    const choisi = employes.find((x) => x.id === ptForm.empId);
                    const contrat = Math.min(+choisi?.heuresJour || 8, JOURNEE_MAX);
                    const fmtPause = (x) => {
                      const pz = pauseDuContrat(x);
                      return pz.minutes + ' min dès ' + pz.debut.replace(':', ' h ');
                    };
                    const depasse = effectif > JOURNEE_MAX + 0.001;
                    return (
                      <div
                        style={{
                          fontSize: 10.5,
                          margin: '2px 0 9px',
                          color: depasse ? C.red : C.muted,
                          fontWeight: depasse ? 800 : 600,
                        }}
                      >
                        {fmtH(effectif)} de travail · contrat {fmtH(contrat)} par jour, pause{' '}
                        {fmtPause(choisi)}
                        {depasse ? ' — ⚠ plus de 9 h : le maximum prévu au contrat' : ''}
                        {!depasse && effectif > contrat + 0.001
                          ? ' — dont ' + fmtH(effectif - contrat) + ' au-delà du contrat'
                          : ''}
                      </div>
                    );
                  })()}
                  <Inp
                    label="Tâche / travail réalisé (optionnel)"
                    value={ptForm.tache || ''}
                    onChange={(e) => setPtForm((p) => ({ ...p, tache: e.target.value }))}
                  />
                  <Btn
                    label="✓ Enregistrer"
                    color={C.amber}
                    sm
                    full
                    onClick={() => {
                      const emp = employes.find((e) => e.id === ptForm.empId);
                      const { editId, pauseManuelle: _pause, ...saisie } = ptForm;
                      upd(selId, (c) => ({
                        ...c,
                        // Une correction remplace le pointage, sous le même
                        // identifiant ; une saisie l'ajoute.
                        pointages:
                          editId != null
                            ? (c.pointages || []).map((x) =>
                                x.id === editId
                                  ? { ...x, ...saisie, empNom: emp?.nom ?? x.empNom }
                                  : x,
                              )
                            : [...(c.pointages || []), { ...saisie, id: uid(), empNom: emp?.nom }],
                      }));
                      setPtForm(null);
                    }}
                  />
                </div>
              </Card>
            )}
            <HeuresPrevues
              chantier={ch}
              employes={employes}
              peutModifier={ACCESS_ROLE !== 'ouvrier'}
            />
            <HeuresParMois pointages={ch.pointages} employes={employes} />
            {(ch.pointages || []).map((p) => {
              const h = hours(p.entree, p.sortie, p.pause);
              return (
                <Card key={p.id} style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      padding: '11px 13px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700 }}>
                        {p.empNom}
                        {p.type && p.type !== 'normal' && (
                          <span
                            style={{
                              fontSize: 7.5,
                              fontWeight: 800,
                              color: '#fff',
                              background:
                                {
                                  sup: C.amber,
                                  supp: C.amber,
                                  deplacement: C.teal,
                                  vacances: C.green,
                                  maladie: C.purple,
                                  accident: C.red,
                                  formation: C.navy,
                                  autre: C.grey,
                                }[p.type] || C.grey,
                              borderRadius: 6,
                              padding: '1px 6px',
                              marginLeft: 6,
                            }}
                          >
                            {{
                              sup: 'Sup.',
                              supp: 'Sup.',
                              deplacement: 'Déplacement',
                              vacances: 'Vacances',
                              maladie: 'Maladie',
                              accident: 'Accident',
                              formation: 'Formation',
                              autre: 'Absence',
                            }[p.type] || p.type}
                          </span>
                        )}
                        {p.lieu === 'ATELIER' && (
                          <span
                            style={{
                              fontSize: 7.5,
                              fontWeight: 800,
                              color: '#fff',
                              background: C.purple,
                              borderRadius: 6,
                              padding: '1px 6px',
                              marginLeft: 6,
                            }}
                          >
                            {tr('🏭 Atelier')}
                          </span>
                        )}
                        {p.onSiteEntree != null && (
                          <span
                            style={{
                              fontSize: 8,
                              fontWeight: 800,
                              color: p.onSiteEntree ? C.green : C.red,
                              marginLeft: 6,
                            }}
                          >
                            {p.onSiteEntree ? '✅ sur site' : '⚠ hors site'}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 8.5, color: C.muted }}>
                        {dateSuisse(p.date)} · {p.entree}→{p.sortie} · pause {p.pause}min
                        {p.tache ? ' — ' + p.tache : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 900,
                          // Un jour passé sans départ : à corriger.
                          color: p.sortie
                            ? C.amber
                            : dateIso(p.date) < aujourdhuiIso()
                              ? C.red
                              : C.teal,
                        }}
                      >
                        {p.sortie
                          ? fmtH(h)
                          : dateIso(p.date) < aujourdhuiIso()
                            ? 'départ manquant'
                            : 'en cours'}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: 4,
                          justifyContent: 'flex-end',
                          marginTop: 4,
                        }}
                      >
                        <button
                          type="button"
                          aria-label="Corriger le pointage"
                          onClick={() =>
                            setPtForm({
                              editId: p.id,
                              empId: p.empId,
                              date: p.date,
                              entree: p.entree || '07:00',
                              sortie: p.sortie || finDeJournee(p.entree || '07:00'),
                              pause: p.sortie
                                ? +p.pause || 0
                                : pauseLegale(p.entree, finDeJournee(p.entree || '07:00')),
                              // Une pause déjà notée (45 min) reste telle quelle
                              // quand seule l'heure de sortie change.
                              pauseManuelle: !!p.sortie,
                              type: p.type || 'normal',
                              tache: p.tache || '',
                              lieu: p.lieu || '',
                            })
                          }
                          style={{
                            border: '1px solid ' + C.border,
                            background: C.white,
                            borderRadius: 7,
                            padding: '3px 7px',
                            fontSize: 11,
                            cursor: 'pointer',
                          }}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          aria-label="Supprimer le pointage"
                          onClick={() => {
                            if (
                              window.confirm(
                                'Supprimer ce pointage ? Les heures en seront retirées.',
                              )
                            )
                              upd(selId, (c) => ({
                                ...c,
                                pointages: (c.pointages || []).filter((x) => x.id !== p.id),
                              }));
                          }}
                          style={{
                            border: '1px solid ' + C.border,
                            background: C.white,
                            borderRadius: 7,
                            padding: '3px 7px',
                            fontSize: 11,
                            cursor: 'pointer',
                            color: C.red,
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
            {/* Ici, seulement les heures. Les travaux du jour (rapports) se
                lisent dans le Journal, le matériel dans son onglet. */}
            {(ch.rapports || []).length > 0 && (
              <button
                type="button"
                onClick={() => setTab('journal')}
                style={{
                  marginTop: 12,
                  width: '100%',
                  border: '1px dashed ' + C.border,
                  background: C.white,
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontSize: 11,
                  color: C.navy,
                  fontWeight: 700,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                📋 Les travaux faits chaque jour (rapports) sont dans 📒 Journal ›
              </button>
            )}
          </div>
        )}

        {tab === 'sous' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <Btn
                label={stForm ? 'Fermer' : '+ Sous-traitant'}
                sm
                color={C.amber}
                onClick={() =>
                  setStForm(
                    stForm
                      ? null
                      : {
                          entreprise: '',
                          metier: '',
                          prixConvenu: 0,
                          paye: 0,
                          photo: '',
                          photoFichier: null,
                        },
                  )
                }
              />
            </div>
            {stForm && (
              <Card style={{ marginBottom: 11, border: '2px solid ' + C.amber }}>
                <Bar
                  icon="🤝"
                  title="Nouveau sous-traitant"
                  color={C.amber}
                  sub="Scanne (photo) sa facture/contrat ou saisis"
                />
                <div style={{ padding: 13 }}>
                  {stForm.photo ? (
                    <div style={{ position: 'relative', marginBottom: 10 }}>
                      <img
                        src={stForm.photo}
                        alt=""
                        style={{
                          width: '100%',
                          maxHeight: 160,
                          objectFit: 'contain',
                          borderRadius: 8,
                          border: '1px solid ' + C.border,
                        }}
                      />
                      <button
                        onClick={() => setStForm((p) => ({ ...p, photo: '', photoFichier: null }))}
                        style={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          background: C.red,
                          border: 'none',
                          borderRadius: 99,
                          color: '#fff',
                          width: 24,
                          height: 24,
                          cursor: 'pointer',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div style={{ marginBottom: 10 }}>
                      <ScanFacture
                        camera
                        label="📷 Scanner la facture sous-traitance (photo)"
                        color={C.amber}
                        onPhoto={(data, fichier) =>
                          setStForm((p) => ({ ...p, photo: data, photoFichier: fichier || null }))
                        }
                        onLecture={(lu) => {
                          const f = champsFacture(lu);
                          setStForm((p) => ({
                            ...p,
                            entreprise: f.fournisseur ?? p.entreprise,
                            metier: f.metier ?? p.metier,
                            prixConvenu: f.montant ?? p.prixConvenu,
                            // La TVA imprimée sur sa facture se récupère telle quelle.
                            montantTva: f.montantTva ?? null,
                          }));
                        }}
                      />
                    </div>
                  )}
                  <Inp
                    label="Entreprise"
                    value={stForm.entreprise}
                    onChange={(e) => setStForm((p) => ({ ...p, entreprise: e.target.value }))}
                  />
                  <Inp
                    label="Métier"
                    value={stForm.metier}
                    onChange={(e) => setStForm((p) => ({ ...p, metier: e.target.value }))}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Inp
                      label={'Prix TTC ' + DEVISE}
                      type="number"
                      value={stForm.prixConvenu}
                      onChange={(e) =>
                        // Un prix corrigé à la main : la TVA lue ne lui correspond plus.
                        setStForm((p) => ({ ...p, prixConvenu: +e.target.value, montantTva: null }))
                      }
                    />
                    <Inp
                      label={'Payé ' + DEVISE}
                      type="number"
                      value={stForm.paye}
                      onChange={(e) => setStForm((p) => ({ ...p, paye: +e.target.value }))}
                    />
                  </div>
                  {/* Sans date de début, le sous-traitant n'avait pas de date
                      sûre : l'écran le datait d'aujourd'hui, le serveur de la
                      création du chantier, et son coût changeait d'année. */}
                  {!ch.debut && (
                    <Inp
                      label={tr('Début du chantier')}
                      type="date"
                      value={stForm.debutChantier || ''}
                      onChange={(e) => setStForm((p) => ({ ...p, debutChantier: e.target.value }))}
                    />
                  )}
                  <Btn
                    label={stForm.editId != null ? '✓ Enregistrer' : '✓ Ajouter'}
                    color={C.amber}
                    sm
                    full
                    disabled={!stForm.entreprise || (!ch.debut && !stForm.debutChantier)}
                    onClick={async () => {
                      const { photoFichier, debutChantier, ...saisie } = stForm;
                      setStForm(null);
                      // Le scan part d'abord, en piece jointe du chantier ; la
                      // ligne en garde la reference et l'affiche a son adresse.
                      const piece = await deposePiece(
                        selId,
                        photoFichier,
                        'Sous-traitant ' + (saisie.entreprise || ''),
                      );
                      // Une correction remplace la ligne, sous le même identifiant.
                      const { editId, ...ligne } = saisie;
                      upd(selId, (c) => ({
                        ...c,
                        ...(debutChantier && !c.debut ? { debut: debutChantier } : {}),
                        sousTraitants:
                          editId != null
                            ? (c.sousTraitants || []).map((x) =>
                                x.id === editId ? { ...x, ...ligne, ...(piece || {}) } : x,
                              )
                            : [
                                ...(c.sousTraitants || []),
                                { ...ligne, ...(piece || { photo: '' }), id: uid() },
                              ],
                      }));
                    }}
                  />
                </div>
              </Card>
            )}
            {(ch.sousTraitants || []).map((s) => {
              const pct =
                s.prixConvenu > 0 ? Math.min(100, Math.round((s.paye / s.prixConvenu) * 100)) : 0;
              return (
                <Card key={s.id} style={{ marginBottom: 9 }}>
                  <div style={{ padding: '12px 14px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 6,
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
                        {s.pieceId ? (
                          // Le scan, PDF ou photo, s'ouvre dans SwissTech comme
                          // celui des factures fournisseurs : un PDF dans une
                          // balise d'image s'affichait cassé.
                          <button
                            type="button"
                            aria-label={tr('Voir la facture')}
                            onClick={() =>
                              ouvrePiece({
                                id: s.pieceId,
                                libelle: 'Facture ' + (s.entreprise || ''),
                              })
                            }
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 6,
                              border: '1px solid ' + C.border,
                              background: '#fff',
                              fontSize: 16,
                              flexShrink: 0,
                              cursor: 'pointer',
                            }}
                          >
                            📎
                          </button>
                        ) : (
                          s.photo &&
                          !/^data:application\/pdf/i.test(s.photo) && (
                            <a
                              href={s.photo}
                              target="_blank"
                              rel="noreferrer"
                              title="Voir la facture en grand"
                            >
                              <img
                                src={s.photo}
                                alt=""
                                style={{
                                  width: 34,
                                  height: 34,
                                  objectFit: 'cover',
                                  borderRadius: 6,
                                  border: '1px solid ' + C.border,
                                  cursor: 'pointer',
                                }}
                              />
                            </a>
                          )
                        )}
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700 }}>{s.entreprise}</div>
                          <div style={{ fontSize: 8.5, color: C.muted }}>{s.metier}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 800 }}>
                        {DEVISE} {fmt(s.prixConvenu)}
                      </div>
                    </div>
                    <div style={{ background: C.border, borderRadius: 99, height: 5 }}>
                      <div
                        style={{
                          width: pct + '%',
                          height: 5,
                          borderRadius: 99,
                          background: C.amber,
                        }}
                      />
                    </div>
                    <div style={{ fontSize: 8, color: C.muted, marginTop: 3 }}>
                      Payé {DEVISE} {fmt(s.paye)} ({pct}%)
                    </div>
                    <PayeLe
                      payeLe={s.payeLe}
                      payePar={s.payePar}
                      onChange={(p) =>
                        upd(selId, (c) => ({
                          ...c,
                          sousTraitants: (c.sousTraitants || []).map((x) =>
                            x.id === s.id ? { ...x, ...p } : x,
                          ),
                        }))
                      }
                    />
                    {/* Une ligne se corrige (le payé avance) ou se retire. */}
                    <div
                      style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}
                    >
                      <Btn
                        label="✎ Modifier"
                        sm
                        ghost
                        color={C.navy}
                        onClick={() => setStForm({ ...s, editId: s.id, photoFichier: null })}
                      />
                      <Btn
                        label="🗑 Retirer"
                        sm
                        ghost
                        danger
                        color={C.red}
                        onClick={() => {
                          if (
                            !window.confirm(
                              trf('Retirer le sous-traitant {0} ?', s.entreprise || ''),
                            )
                          )
                            return;
                          upd(selId, (c) => ({
                            ...c,
                            sousTraitants: (c.sousTraitants || []).filter((x) => x.id !== s.id),
                          }));
                        }}
                      />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {tab === 'journal' && <JournalChantier ch={ch} employes={employes} />}

        {tab === 'materiel' &&
          (() => {
            // Le prix d'achat reste au bureau : un compte qui ne le voit pas
            // reçoit des lignes sans prix.
            const lignes = [...(ch.materiaux || [])].reverse();
            const voitPrix = ACCESS_ROLE === 'patron' || lignes.some((m) => +m.pa > 0);
            return (
              <>
                <Card>
                  <Bar
                    icon="📦"
                    title="Matériel du chantier"
                    color={C.teal}
                    sub="Pris au dépôt ou acheté par l'équipe, au prix d'achat"
                  />
                  <div style={{ padding: '4px 13px 13px' }}>
                    {lignes.length === 0 ? (
                      <div
                        style={{ padding: 14, textAlign: 'center', fontSize: 11, color: C.muted }}
                      >
                        Aucun matériel pour ce chantier. L&apos;ouvrier le prend au dépôt depuis son
                        espace (🚚 Matériel) : le stock baisse et le coût s&apos;inscrit ici.
                      </div>
                    ) : (
                      lignes.map((m, i) => {
                        const retour = +m.qte < 0;
                        return (
                          <div
                            key={m.id ?? i}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 8,
                              padding: '9px 0',
                              borderBottom: '1px solid ' + C.border,
                              fontSize: 11,
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 700, color: C.ink }}>
                                {(retour ? '↩️ ' : m.source === 'fournisseur' ? '🏭 ' : '📤 ') +
                                  (m.nom || m.d) +
                                  ' × ' +
                                  Math.abs(+m.qte || 0) +
                                  (m.u ? ' ' + m.u : '')}
                              </div>
                              <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>
                                {[m.date, m.heure, m.empNom].filter(Boolean).join(' · ')}
                              </div>
                              {/* Le reste d'un achat chez le fournisseur revient au dépôt. */}
                              <RetourAchatDepot
                                chantierId={ch.id}
                                achat={m}
                                lignes={ch.materiaux}
                                onFait={(r) => {
                                  const frais = dtoVersChantierDetail(r);
                                  upd(ch.id, (c) => ({
                                    ...c,
                                    materiaux: frais.materiaux,
                                    _connus: {
                                      ...(c._connus || {}),
                                      materiaux: frais._connus.materiaux,
                                    },
                                  }));
                                }}
                              />
                            </div>
                            {voitPrix ? (
                              <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <div style={{ fontWeight: 800, color: retour ? C.green : C.red }}>
                                  {DEVISE + ' ' + fmt((+m.pa || 0) * (+m.qte || 0))}
                                </div>
                                <div style={{ fontSize: 9, color: C.muted }}>
                                  {Math.abs(+m.qte || 0) + ' × ' + fmt(+m.pa || 0)}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })
                    )}
                    {voitPrix && lignes.length > 0 ? (
                      <FR
                        label="Total du matériel"
                        value={DEVISE + ' ' + fmt(mat)}
                        color={C.red}
                        bold
                        last
                      />
                    ) : null}
                  </div>
                </Card>
                <Card style={{ marginTop: 10 }}>
                  <div style={{ padding: '6px 13px 12px' }}>
                    <DocumentsJoints
                      genre="CHANTIER"
                      documentId={ch.id}
                      sortes={['Bon de livraison', 'Facture de matériel']}
                      titre={tr('📎 BONS DE LIVRAISON ET FACTURES DU MATÉRIEL')}
                      filtre
                    />
                  </div>
                </Card>
              </>
            );
          })()}

        {tab === 'facf' && (
          <div>
            {/* Les dépenses rattachées au chantier (Dépenses, tickets des bons
                d'achat) : elles comptaient dans les matériaux sans paraître
                nulle part sur le chantier. */}
            {fraisDuChantier(ch, frais).length > 0 && (
              <Card style={{ marginBottom: 11 }}>
                <Bar
                  icon="🧾"
                  title="Dépenses du chantier"
                  color={C.amber}
                  sub="Saisies dans Dépenses ou par l’employé (ticket du bon d’achat)"
                />
                <div style={{ padding: '4px 13px 10px' }}>
                  {fraisDuChantier(ch, frais).map((x) => (
                    <div
                      key={x.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 8,
                        padding: '7px 0',
                        borderBottom: '1px solid ' + C.border,
                        fontSize: 11,
                      }}
                    >
                      <span style={{ minWidth: 0 }}>
                        <b>{x.libelle || x.fournisseur || x.type}</b>
                        <span style={{ display: 'block', fontSize: 9.5, color: C.muted }}>
                          {[x.date, x.fournisseur].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      <b style={{ whiteSpace: 'nowrap' }}>
                        {DEVISE} {fmt(+x.montant || 0)}
                      </b>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            {achatsJoints.length > 0 && (
              <Card style={{ marginBottom: 11 }}>
                <Bar
                  icon="🧾"
                  title="Ajoutées sur les offres et factures"
                  color={C.purple}
                  sub="Dépenses de matériel et sous-traitance jointes au client"
                />
                <div style={{ padding: '4px 13px 10px' }}>
                  <ListeDuClient
                    devis={docsOuverts.devis}
                    factures={docsOuverts.factures}
                    pieces={piecesOuvertes}
                    sansPhotos
                  />
                </div>
              </Card>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginBottom: 10 }}>
              {/* Envoyer un employé acheter le matériel de ce chantier chez un
                  fournisseur : le bon s'ouvre déjà sur lui. */}
              {typeof selId === 'number' && (
                <Btn
                  label="🧾 Bon d’achat"
                  sm
                  ghost
                  color={C.green}
                  onClick={() => onNav('bonsachat', { chantierId: selId })}
                />
              )}
              <Btn
                label={facForm ? 'Fermer' : '+ Facture fourn.'}
                sm
                color={C.purple}
                onClick={() =>
                  setFacForm(
                    facForm
                      ? null
                      : {
                          fournisseur: '',
                          montant: 0,
                          ref: '',
                          cat: 'Matériaux',
                          photo: '',
                          photoFichier: null,
                        },
                  )
                }
              />
            </div>
            {facForm && (
              <Card style={{ marginBottom: 11, border: '2px solid ' + C.purple }}>
                <Bar
                  icon="📥"
                  title="Facture fournisseur"
                  color={C.purple}
                  sub="Scanne (photo) ou saisis"
                />
                <div style={{ padding: 13 }}>
                  {/* La lecture reste à l'écran sous la photo : elle partait avec
                      le bouton, et ni « lecture en cours » ni une erreur ne se
                      voyaient — les champs restaient vides sans un mot. */}
                  {facForm.photo && (
                    <div style={{ position: 'relative', marginBottom: 10 }}>
                      {String(facForm.photo).startsWith('data:application/pdf') ? (
                        <div
                          style={{
                            border: '1px solid ' + C.border,
                            borderRadius: 8,
                            padding: '14px 12px',
                            fontSize: 11,
                            fontWeight: 700,
                            color: C.ink,
                          }}
                        >
                          {'📄 ' + (facForm.photoFichier?.name || tr('Facture en PDF jointe'))}
                        </div>
                      ) : (
                        <PhotoAgrandie src={facForm.photo}>
                          <img
                            src={facForm.photo}
                            alt=""
                            style={{
                              width: '100%',
                              maxHeight: 160,
                              objectFit: 'contain',
                              borderRadius: 8,
                              border: '1px solid ' + C.border,
                            }}
                          />
                        </PhotoAgrandie>
                      )}
                      <button
                        onClick={() => setFacForm((p) => ({ ...p, photo: '', photoFichier: null }))}
                        style={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          background: C.red,
                          border: 'none',
                          borderRadius: 99,
                          color: '#fff',
                          width: 24,
                          height: 24,
                          cursor: 'pointer',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  <div style={{ marginBottom: 10 }}>
                    <ScanFacture
                      camera
                      sansBouton={!!facForm.photo}
                      label="📷 Scanner la facture (photo)"
                      color={C.purple}
                      onPhoto={(data, fichier) =>
                        setFacForm((p) => ({ ...p, photo: data, photoFichier: fichier || null }))
                      }
                      onLecture={(lu) => {
                        const f = champsFacture(lu);
                        setFacForm((p) => ({
                          ...p,
                          fournisseur: f.fournisseur ?? p.fournisseur,
                          iban: f.iban ?? p.iban,
                          ref: f.ref ?? p.ref,
                          montant: f.montant ?? p.montant,
                          montantTva: f.montantTva ?? p.montantTva,
                          tauxTva: f.tauxTva ?? p.tauxTva,
                          // La date de la facture, lue avec elle : le jour du
                          // scan décalait la période de TVA.
                          date: f.date ? isoVersDate(f.date) : p.date,
                          // « Ça m'a pas mis le matériel » : les articles lus
                          // sur la facture, avec leur quantité.
                          articles: articlesLus(lu) || p.articles || '',
                        }));
                      }}
                    />
                  </div>
                  <textarea
                    aria-label={tr('Articles achetés')}
                    value={facForm.articles || ''}
                    onChange={(e) =>
                      setFacForm((p) => ({ ...p, articles: e.target.value.slice(0, 300) }))
                    }
                    placeholder={tr('Articles achetés (lus tout seuls sur la facture)')}
                    rows={5}
                    style={{
                      ..._inp,
                      marginBottom: 10,
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      lineHeight: 1.45,
                    }}
                  />
                  <Inp
                    label="Fournisseur"
                    value={facForm.fournisseur}
                    onChange={(e) => setFacForm((p) => ({ ...p, fournisseur: e.target.value }))}
                  />
                  <Inp
                    label="IBAN du fournisseur (pour payer)"
                    value={facForm.iban || ''}
                    onChange={(e) => setFacForm((p) => ({ ...p, iban: e.target.value }))}
                    placeholder="CH.. (optionnel)"
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <Inp
                        label="Référence"
                        value={facForm.ref}
                        onChange={(e) => setFacForm((p) => ({ ...p, ref: e.target.value }))}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Inp
                        label={'Montant ' + DEVISE}
                        type="number"
                        value={facForm.montant}
                        onChange={(e) => setFacForm((p) => ({ ...p, montant: +e.target.value }))}
                      />
                    </div>
                  </div>
                  <Inp
                    label="Date de la facture"
                    type="date"
                    value={dateVersIso(facForm.date || today()) || ''}
                    onChange={(e) =>
                      setFacForm((p) => ({ ...p, date: isoVersDate(e.target.value) }))
                    }
                  />
                  <Inp
                    label={'TVA ' + DEVISE + ' (sur la facture)'}
                    type="number"
                    value={facForm.montantTva ?? ''}
                    onChange={(e) =>
                      setFacForm((p) => ({
                        ...p,
                        montantTva: e.target.value === '' ? null : +e.target.value,
                      }))
                    }
                    placeholder="Vide : calculée au taux normal"
                  />
                  {/* Le taux de la facture quand sa TVA n'est pas saisie :
                      les émoluments de la commune ou un fournisseur non
                      assujetti sont sans TVA ; sans choix, le taux normal
                      s'appliquait à tout et récupérait une TVA qui n'existe pas. */}
                  {(() => {
                    const t = tauxTvaEnVigueur(soc, facForm.date || today());
                    return (
                      <label style={{ display: 'block', marginBottom: 10 }}>
                        <span
                          style={{
                            display: 'block',
                            fontSize: 9,
                            fontWeight: 700,
                            color: C.muted,
                            marginBottom: 5,
                          }}
                        >
                          {tr('Taux de TVA')}
                        </span>
                        <select
                          aria-label={tr('Taux de TVA')}
                          value={facForm.tauxTva ?? ''}
                          onChange={(e) =>
                            setFacForm((p) => ({
                              ...p,
                              tauxTva: e.target.value === '' ? null : +e.target.value,
                            }))
                          }
                          style={{ ..._inp, marginBottom: 0 }}
                        >
                          <option value="">{tr('Taux normal') + ' ' + t.normal + ' %'}</option>
                          <option value={t.reduit}>
                            {tr('Taux réduit') + ' ' + t.reduit + ' %'}
                          </option>
                          <option value={t.hebergement}>
                            {tr('Hébergement') + ' ' + t.hebergement + ' %'}
                          </option>
                          <option value={0}>{tr('Sans TVA') + ' 0 %'}</option>
                        </select>
                      </label>
                    );
                  })()}
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, marginBottom: 5 }}>
                    Catégorie
                  </div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
                    {metierCats(soc, false).map((c) => (
                      <div
                        key={c}
                        onClick={() => setFacForm((p) => ({ ...p, cat: c }))}
                        style={{
                          padding: '5px 9px',
                          borderRadius: 8,
                          fontSize: 9,
                          fontWeight: 700,
                          cursor: 'pointer',
                          border: '1px solid ' + (facForm.cat === c ? C.purple : C.border),
                          background: facForm.cat === c ? C.purple : C.white,
                          color: facForm.cat === c ? '#fff' : C.grey,
                        }}
                      >
                        {c}
                      </div>
                    ))}
                  </div>
                  <Btn
                    label="✓ Enregistrer"
                    color={C.purple}
                    sm
                    full
                    disabled={!facForm.montant}
                    onClick={async () => {
                      const { photoFichier, ...saisie } = facForm;
                      setFacForm(null);
                      // Le scan part d'abord, en piece jointe du chantier ; la
                      // facture en garde la reference. L'IBAN voyage avec elle.
                      const piece = await deposePiece(
                        selId,
                        photoFichier,
                        'Facture ' + (saisie.fournisseur || 'fournisseur'),
                      );
                      // Une correction remplace la facture, sous le même identifiant.
                      const { editId, ...ligne } = saisie;
                      upd(selId, (c) => ({
                        ...c,
                        factures:
                          editId != null
                            ? (c.factures || []).map((x) =>
                                x.id === editId
                                  ? { ...x, ...ligne, ...(piece || {}), date: ligne.date || x.date }
                                  : x,
                              )
                            : [
                                ...(c.factures || []),
                                {
                                  ...ligne,
                                  ...(piece || { photo: '' }),
                                  id: uid(),
                                  date: ligne.date || today(),
                                },
                              ],
                      }));
                    }}
                  />
                </div>
              </Card>
            )}
            {(ch.factures || []).map((f) => (
              <Card key={f.id} style={{ marginBottom: 8 }}>
                <div
                  style={{
                    padding: '11px 13px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <div
                    style={{ display: 'flex', gap: 9, alignItems: 'center', flex: 1, minWidth: 0 }}
                  >
                    {f.pieceId ? (
                      // La facture jointe, PDF ou photo, s'ouvre dans SwissTech.
                      <button
                        type="button"
                        aria-label={tr('Voir la facture')}
                        onClick={() =>
                          ouvrePiece({ id: f.pieceId, libelle: 'Facture ' + (f.fournisseur || '') })
                        }
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 6,
                          border: '1px solid ' + C.border,
                          background: '#fff',
                          fontSize: 16,
                          flexShrink: 0,
                          cursor: 'pointer',
                        }}
                      >
                        📎
                      </button>
                    ) : (
                      f.photo && (
                        <PhotoAgrandie src={f.photo} alt={f.fournisseur || ''}>
                          <img
                            src={f.photo}
                            alt=""
                            style={{
                              width: 34,
                              height: 34,
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: '1px solid ' + C.border,
                              flexShrink: 0,
                              cursor: 'pointer',
                            }}
                          />
                        </PhotoAgrandie>
                      )
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700 }}>
                        {f.fournisseur || 'Facture'}
                      </div>
                      <div style={{ fontSize: 8.5, color: C.muted }}>
                        {[f.cat, f.ref, f.date].filter(Boolean).join(' · ')}
                      </div>
                      {f.articles && (
                        <div
                          style={{ fontSize: 8.5, color: C.grey, marginTop: 2, lineHeight: 1.4 }}
                        >
                          {f.articles}
                        </div>
                      )}
                      {/* Le jour où elle a été payée : à la main, ou trouvé dans le relevé. */}
                      <PayeLe
                        payeLe={f.payeLe}
                        payePar={f.payePar}
                        onChange={(p) =>
                          upd(selId, (c) => ({
                            ...c,
                            factures: (c.factures || []).map((x) =>
                              x.id === f.id ? { ...x, ...p } : x,
                            ),
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 6,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 800, color: C.purple }}>
                      {DEVISE} {fmt(f.montant)}
                    </div>
                    {f.iban && (
                      <button
                        onClick={() => setPayQrId(payQrId === f.id ? null : f.id)}
                        style={{
                          background: payQrId === f.id ? C.navy : C.navyXL,
                          color: payQrId === f.id ? '#fff' : C.navy,
                          border: 'none',
                          borderRadius: 8,
                          padding: '5px 9px',
                          fontSize: 8.5,
                          fontWeight: 800,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        💳 Payer (QR)
                      </button>
                    )}
                    {/* Le nom lu sur le PDF (toute l'adresse), un montant faux :
                        la facture se corrige, ou se retire. */}
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        type="button"
                        aria-label={tr('Modifier la facture fournisseur')}
                        onClick={() => setFacForm({ ...f, editId: f.id, photoFichier: null })}
                        style={{
                          background: C.navyXL,
                          color: C.navy,
                          border: 'none',
                          borderRadius: 8,
                          padding: '5px 8px',
                          fontSize: 10,
                          cursor: 'pointer',
                        }}
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        aria-label={tr('Retirer la facture fournisseur')}
                        onClick={() => {
                          if (
                            !window.confirm(
                              trf('Retirer la facture {0} ?', f.fournisseur || f.ref || ''),
                            )
                          )
                            return;
                          upd(selId, (c) => ({
                            ...c,
                            factures: (c.factures || []).filter((x) => x.id !== f.id),
                          }));
                        }}
                        style={{
                          background: C.redL,
                          color: C.red,
                          border: 'none',
                          borderRadius: 8,
                          padding: '5px 8px',
                          fontSize: 10,
                          cursor: 'pointer',
                        }}
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                </div>
                {f.iban && payQrId === f.id && (
                  <div style={{ padding: '0 13px 13px', textAlign: 'center' }}>
                    <div
                      style={{
                        display: 'inline-block',
                        background: '#fff',
                        padding: 8,
                        borderRadius: 8,
                        border: '1px solid ' + C.border,
                      }}
                    >
                      <QRCode
                        size={132}
                        color={C.ink}
                        value={swissQR({
                          iban: f.iban,
                          nom: f.fournisseur || 'Fournisseur',
                          adr: '',
                          npa: '',
                          amount: f.montant,
                          clientNom: soc?.nom || '',
                          clientAdr: soc?.adr || '',
                          clientNpa: soc?.npa || '',
                          refType: 'NON',
                          ref: '',
                          msg: f.ref || f.fournisseur || 'Facture',
                        })}
                      />
                    </div>
                    <div style={{ fontSize: 8.5, color: C.muted, marginTop: 6 }}>
                      Scanne ce QR dans ton e-banking pour payer le fournisseur
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {tab === 'reception' && (
          <div>
            {ch.pv ? (
              <Card>
                <Bar icon="✅" title="PV de réception signé" color={C.green} />
                <div style={{ padding: 13 }}>
                  {/* Le montant du chantier est hors taxe : affiché sous
                      « Travaux TTC », il donnait aussi une retenue trop basse.
                      Le TTC est celui de l'offre et de ses avenants. */}
                  <FR label="Travaux TTC" value={DEVISE + ' ' + fmt(travauxTtc)} />
                  <FR
                    label={'Retenue garantie ' + ch.pv.retenue + '%'}
                    value={'− ' + DEVISE + ' ' + fmt(retenueTtc)}
                  />
                  <FR
                    label="À libérer"
                    value={DEVISE + ' ' + fmt(travauxTtc - retenueTtc)}
                    color={C.green}
                    bold
                    last
                  />
                  {ch.pv.reserves && (
                    <div
                      style={{
                        background: C.amberL,
                        borderRadius: 8,
                        padding: '8px 11px',
                        marginTop: 8,
                        fontSize: 9,
                        color: C.amber,
                      }}
                    >
                      Réserves : {ch.pv.reserves}
                    </div>
                  )}
                </div>
              </Card>
            ) : (
              <Card style={{ border: '2px solid ' + C.green }}>
                <Bar icon="✅" title="Établir le PV de réception" color={C.green} />
                <div style={{ padding: 13 }}>
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.muted,
                      marginBottom: 5,
                      textTransform: 'uppercase',
                    }}
                  >
                    Retenue de garantie
                  </div>
                  <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
                    {[0, 5, 10].map((r) => (
                      <div
                        key={r}
                        onClick={() =>
                          setPvForm((p) => ({ ...(p || { reserves: '' }), retenue: r }))
                        }
                        style={{
                          padding: '7px 14px',
                          borderRadius: 8,
                          fontSize: 10,
                          fontWeight: 700,
                          cursor: 'pointer',
                          background: (pvForm?.retenue ?? 5) === r ? C.green : C.white,
                          color: (pvForm?.retenue ?? 5) === r ? '#fff' : C.grey,
                          border: '1px solid ' + C.border,
                        }}
                      >
                        {r}%
                      </div>
                    ))}
                  </div>
                  <Inp
                    label="Réserves (vide si aucune)"
                    rows={2}
                    value={pvForm?.reserves || ''}
                    onChange={(e) =>
                      setPvForm((p) => ({ ...(p || { retenue: 5 }), reserves: e.target.value }))
                    }
                  />
                  <Btn
                    label="✓ Valider la réception"
                    color={C.green}
                    full
                    onClick={() => {
                      const pv = {
                        retenue: pvForm?.retenue ?? 5,
                        reserves: pvForm?.reserves || '',
                        date: today(),
                      };
                      upd(selId, (c) => ({ ...c, pv, statut: 'Terminé', av: 100 }));
                      setPvForm(null);
                    }}
                  />
                </div>
              </Card>
            )}
            <Card style={{ marginTop: 10 }}>
              <div style={{ padding: '6px 13px 12px' }}>
                <DocumentsJoints
                  genre="CHANTIER"
                  documentId={ch.id}
                  sortes={['PV de réception signé', 'Photo des réserves', 'Levée des réserves']}
                  titre={tr('📎 PV SIGNÉ ET PHOTOS DES RÉSERVES')}
                  filtre
                />
              </div>
            </Card>
          </div>
        )}

        {tab === 'photos' && (
          <div>
            {/* Les photos d'abord : elles suivaient le formulaire et l'avis de
                conservation, et le patron ne les trouvait qu'en faisant défiler. */}
            {(() => {
              const toutes = ch.photos || [];
              const tousPostes = [...new Set(toutes.map((p) => p.poste || 'Général'))];
              // Retrouver la preuve d'un travail (« Étanchéité ») parmi toutes
              // les photos du chantier.
              const phs =
                photoFil === 'tous'
                  ? toutes
                  : toutes.filter((p) => (p.poste || 'Général') === photoFil);
              const postes = [...new Set(phs.map((p) => p.poste || 'Général'))];
              return (
                <>
                  {tousPostes.length > 1 ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                      {['tous', ...tousPostes].map((ps) => (
                        <button
                          key={ps}
                          type="button"
                          aria-pressed={photoFil === ps}
                          onClick={() => setPhotoFil(ps)}
                          style={{
                            padding: '6px 11px',
                            borderRadius: 99,
                            fontSize: 10,
                            fontWeight: 700,
                            cursor: 'pointer',
                            border: '1px solid ' + (photoFil === ps ? C.navy : C.border),
                            background: photoFil === ps ? C.navy : C.white,
                            color: photoFil === ps ? '#fff' : C.grey,
                          }}
                        >
                          {ps === 'tous' ? 'Toutes (' + toutes.length + ')' : ps}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {postes.map((ps) => (
                    <div key={ps} style={{ marginBottom: 12 }}>
                      <div
                        style={{ fontSize: 11, fontWeight: 800, color: C.ink, margin: '4px 0 7px' }}
                      >
                        📌 {ps}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
                        {phs
                          .filter((p) => (p.poste || 'Général') === ps)
                          .map((p) => (
                            <div
                              key={p.id}
                              style={{
                                borderRadius: 12,
                                overflow: 'hidden',
                                border: '1px solid ' + C.border,
                              }}
                            >
                              <div
                                style={{
                                  height: 78,
                                  background: C.navyXL,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 26,
                                  overflow: 'hidden',
                                  position: 'relative',
                                }}
                              >
                                {p.src ? (
                                  <PhotoAgrandie src={p.src} alt={p.note || p.label || ''} />
                                ) : (
                                  p.emoji
                                )}
                                <span
                                  style={{
                                    position: 'absolute',
                                    top: 5,
                                    left: 5,
                                    fontSize: 7,
                                    fontWeight: 800,
                                    color: '#fff',
                                    background: 'rgba(0,0,0,.5)',
                                    borderRadius: 5,
                                    padding: '1px 6px',
                                  }}
                                >
                                  {{
                                    avant: 'AVANT',
                                    apres: 'APRÈS',
                                    pendant: 'PENDANT',
                                    probleme: 'PROBLÈME',
                                  }[p.cat] || ''}
                                </span>
                              </div>
                              <div style={{ padding: '7px 9px', position: 'relative' }}>
                                <div style={{ fontSize: 8.5, fontWeight: 700 }}>
                                  {p.note || 'Photo'}
                                </div>
                                <div style={{ fontSize: 7.5, color: C.muted, marginBottom: 6 }}>
                                  {[p.date, p.h, p.auteur].filter(Boolean).join(' · ')}
                                </div>
                                <div
                                  onClick={() =>
                                    upd(selId, (c) => ({
                                      ...c,
                                      photos: c.photos.map((x) =>
                                        x.id === p.id
                                          ? { ...x, partageClient: !x.partageClient }
                                          : x,
                                      ),
                                    }))
                                  }
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 4,
                                    borderRadius: 7,
                                    padding: '5px',
                                    fontSize: 8,
                                    fontWeight: 800,
                                    cursor: 'pointer',
                                    background: p.partageClient ? C.green : '#eef1f5',
                                    color: p.partageClient ? '#fff' : C.grey,
                                  }}
                                >
                                  {p.partageClient ? '👁 Le client voit' : '🔒 Interne'}
                                </div>
                                <button
                                  aria-label="Supprimer la photo"
                                  onClick={() => {
                                    // Une photo est souvent la seule preuve d'un travail
                                    // fait : elle ne part pas sur un clic malheureux.
                                    if (
                                      window.confirm(
                                        'Supprimer définitivement cette photo ? Elle peut servir de preuve si le client conteste un jour.',
                                      )
                                    )
                                      retirePhoto(selId, p.id);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: 5,
                                    right: 6,
                                    background: 'none',
                                    border: 'none',
                                    color: C.muted,
                                    cursor: 'pointer',
                                    fontSize: 11,
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </>
              );
            })()}
            {(ch.photos || []).length === 0 && (
              <div style={{ textAlign: 'center', padding: 24, color: C.muted, fontSize: 10 }}>
                Aucune photo sur ce chantier pour le moment.
              </div>
            )}
            <PhotosDeLOffre devisId={ch.devisId} />
            <Card style={{ marginBottom: 11 }}>
              <Bar
                icon="📷"
                title="Photo d'avancement"
                color={C.teal}
                sub="Prise avec l'appareil · interne jusqu'à ce que tu la partages"
              />
              <div style={{ padding: 13 }}>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: C.muted,
                    marginBottom: 5,
                    textTransform: 'uppercase',
                  }}
                >
                  Poste (repris de l'offre)
                </div>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 9 }}>
                  {(ch.postes && ch.postes.length ? ch.postes : ['Général']).map((ps) => (
                    <div
                      key={ps}
                      onClick={() => setChPhotoPoste(ps)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: 8,
                        fontSize: 9,
                        fontWeight: 700,
                        cursor: 'pointer',
                        border:
                          '1px solid ' +
                          ((chPhotoPoste || (ch.postes && ch.postes[0]) || 'Général') === ps
                            ? C.navy
                            : C.border),
                        background:
                          (chPhotoPoste || (ch.postes && ch.postes[0]) || 'Général') === ps
                            ? C.navy
                            : C.white,
                        color:
                          (chPhotoPoste || (ch.postes && ch.postes[0]) || 'Général') === ps
                            ? '#fff'
                            : C.grey,
                      }}
                    >
                      {ps}
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: C.muted,
                    marginBottom: 5,
                    textTransform: 'uppercase',
                  }}
                >
                  Étape
                </div>
                <div style={{ display: 'flex', gap: 5, marginBottom: 9 }}>
                  {[
                    ['avant', '🧱 Avant'],
                    ['apres', '✨ Après'],
                    ['pendant', '🔨 Pendant'],
                    ['probleme', '⚠ Problème'],
                  ].map(([cat, lb]) => (
                    <div
                      key={cat}
                      onClick={() => setChPhotoCat(cat)}
                      style={{
                        flex: 1,
                        textAlign: 'center',
                        padding: '7px 3px',
                        borderRadius: 8,
                        fontSize: 8.5,
                        fontWeight: 700,
                        cursor: 'pointer',
                        border: '1px solid ' + (chPhotoCat === cat ? C.teal : C.border),
                        background: chPhotoCat === cat ? C.teal : C.white,
                        color: chPhotoCat === cat ? '#fff' : C.grey,
                      }}
                    >
                      {lb}
                    </div>
                  ))}
                </div>
                <input
                  value={chPhotoNote}
                  onChange={(e) => setChPhotoNote(e.target.value)}
                  placeholder="Note (facultatif)"
                  style={inpFS}
                />
                <PhotoBtn
                  label="📷 Prendre la photo"
                  color={C.teal}
                  full
                  onPhoto={(apercu, fichier) => {
                    const em =
                      { avant: '🧱', pendant: '🔨', apres: '✨', probleme: '⚠' }[chPhotoCat] ||
                      '📷';
                    const ps = chPhotoPoste || (ch.postes && ch.postes[0]) || 'Général';
                    // Le fichier part tel quel, sans passer par le chantier :
                    // l'encoder dans l'agrégat le gonflait d'un tiers et
                    // renvoyait toutes les photos à chaque enregistrement.
                    deposePhoto(selId, fichier, {
                      cat: chPhotoCat,
                      categorie: chPhotoCat,
                      poste: ps,
                      emoji: em,
                      label: (chPhotoNote.trim() || ps) + ' — ' + today(),
                      libelle: (chPhotoNote.trim() || ps) + ' — ' + today(),
                      note: chPhotoNote.trim(),
                      date: today(),
                      auteur: 'Bureau',
                      partageClient: false,
                    });
                    setChPhotoNote('');
                  }}
                />
                {photoErreur && (
                  <div
                    role="alert"
                    style={{
                      marginTop: 8,
                      fontSize: 11,
                      color: C.red,
                      fontWeight: 700,
                      lineHeight: 1.5,
                    }}
                  >
                    {photoErreur}
                  </div>
                )}
              </div>
            </Card>
            <div
              role="note"
              style={{
                fontSize: 9,
                color: '#92400E',
                textAlign: 'center',
                padding: '9px 11px',
                background: '#FEF3C7',
                border: '1px solid #FCD34D',
                borderRadius: 10,
                marginBottom: 11,
                lineHeight: 1.45,
              }}
            >
              ℹ️ Les photos sont conservées <b>{PHOTO_ANS} ans</b> après la fin du chantier, puis
              supprimées automatiquement. Les factures et documents comptables restent conservés{' '}
              {DOCUMENTS_ANS} ans.
            </div>
          </div>
        )}

        {tab === 'messages' && (
          <div>
            <div
              style={{
                background: C.white,
                borderRadius: 12,
                border: '1px solid ' + C.border,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: 12,
                  maxHeight: 300,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                {(ch.messages || []).map((m) => {
                  const isG = m.de === 'gerant';
                  return (
                    <div
                      key={m.id}
                      style={{
                        display: 'flex',
                        flexDirection: isG ? 'row-reverse' : 'row',
                        gap: 7,
                      }}
                    >
                      <div style={{ maxWidth: '78%' }}>
                        <div
                          style={{
                            background: isG ? C.navy : C.navyXL,
                            color: isG ? '#fff' : C.ink,
                            borderRadius: 10,
                            padding: '8px 11px',
                            fontSize: 10,
                          }}
                        >
                          {m.contenu}
                        </div>
                        <div
                          style={{
                            fontSize: 7,
                            color: C.muted,
                            marginTop: 2,
                            textAlign: isG ? 'right' : 'left',
                          }}
                        >
                          {dateSuisse(m.date)} {m.h}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(ch.messages || []).length === 0 && (
                  <div style={{ textAlign: 'center', color: C.muted, fontSize: 10, padding: 20 }}>
                    Aucun message
                  </div>
                )}
              </div>
              <div
                style={{
                  padding: '10px 12px',
                  borderTop: '1px solid ' + C.border,
                  background: C.warm,
                  display: 'flex',
                  gap: 8,
                }}
              >
                <input
                  value={msgForm}
                  onChange={(e) => setMsgForm(e.target.value)}
                  placeholder="Message à l'employé…"
                  style={{
                    flex: 1,
                    border: '1px solid ' + C.border,
                    borderRadius: 9,
                    padding: '8px 11px',
                    fontSize: 10,
                  }}
                />
                <Btn
                  label="➤"
                  sm
                  color={C.navy}
                  disabled={!msgForm.trim()}
                  onClick={() => {
                    upd(selId, (c) => ({
                      ...c,
                      messages: [
                        ...(c.messages || []),
                        {
                          id: uid(),
                          de: 'gerant',
                          contenu: msgForm,
                          date: today().slice(0, 5),
                          h: '',
                          lu: true,
                        },
                      ],
                    }));
                    setMsgForm('');
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {tab === 'avenants' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
              <Btn
                label={avForm ? 'Fermer' : '+ Avenant'}
                sm
                color={C.purple}
                onClick={() => {
                  setAvForm(
                    avForm
                      ? null
                      : { motif: '', lignes: [{ id: uid(), d: '', q: 1, u: 'u', p: 0 }] },
                  );
                  setAvSign('');
                }}
              />
            </div>
            {avForm &&
              (() => {
                const tot = avForm.lignes.reduce((s, l) => s + (+l.q || 0) * (+l.p || 0), 0);
                return (
                  <Card style={{ marginBottom: 11, border: '2px solid ' + C.purple }}>
                    <Bar
                      icon="➕"
                      title="Nouvel avenant"
                      color={C.purple}
                      sub="Travaux supplémentaires — ajouté au montant du chantier"
                    />
                    <div style={{ padding: 13 }}>
                      <Inp
                        label="Motif"
                        value={avForm.motif}
                        onChange={(e) => setAvForm((p) => ({ ...p, motif: e.target.value }))}
                        placeholder="Ex : reprise mur porteur non prévue"
                      />
                      {avForm.lignes.map((l) => (
                        <div
                          key={l.id}
                          style={{
                            background: C.navyXL,
                            borderRadius: 9,
                            padding: 9,
                            marginBottom: 7,
                          }}
                        >
                          <input
                            value={l.d}
                            placeholder="Désignation"
                            onChange={(e) =>
                              setAvForm((p) => ({
                                ...p,
                                lignes: p.lignes.map((x) =>
                                  x.id === l.id ? { ...x, d: e.target.value } : x,
                                ),
                              }))
                            }
                            style={{
                              width: '100%',
                              border: '1px solid ' + C.border,
                              borderRadius: 7,
                              padding: '7px 9px',
                              fontSize: 10,
                              marginBottom: 6,
                            }}
                          />
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <NumInput
                              value={l.q}
                              onChange={(e) =>
                                setAvForm((p) => ({
                                  ...p,
                                  lignes: p.lignes.map((x) =>
                                    x.id === l.id ? { ...x, q: e.target.value } : x,
                                  ),
                                }))
                              }
                              style={{
                                width: 50,
                                border: '1px solid ' + C.border,
                                borderRadius: 7,
                                padding: '5px',
                                fontSize: 10,
                                textAlign: 'center',
                              }}
                            />
                            {!prixOk && (
                              <input
                                aria-label={tr('Unité')}
                                value={l.u || ''}
                                placeholder={tr('m², h, ml…')}
                                onChange={(e) =>
                                  setAvForm((p) => ({
                                    ...p,
                                    lignes: p.lignes.map((x) =>
                                      x.id === l.id ? { ...x, u: e.target.value } : x,
                                    ),
                                  }))
                                }
                                style={{
                                  width: 70,
                                  border: '1px solid ' + C.border,
                                  borderRadius: 7,
                                  padding: '5px',
                                  fontSize: 10,
                                  textAlign: 'center',
                                }}
                              />
                            )}
                            {prixOk && <span style={{ fontSize: 11, color: C.muted }}>×</span>}
                            {prixOk && (
                              <NumInput
                                value={l.p}
                                onChange={(e) =>
                                  setAvForm((p) => ({
                                    ...p,
                                    lignes: p.lignes.map((x) =>
                                      x.id === l.id ? { ...x, p: e.target.value } : x,
                                    ),
                                  }))
                                }
                                style={{
                                  width: 70,
                                  border: '1px solid ' + C.border,
                                  borderRadius: 7,
                                  padding: '5px',
                                  fontSize: 10,
                                  textAlign: 'center',
                                }}
                              />
                            )}
                            {prixOk && (
                              <span style={{ fontSize: 9, color: C.muted }}>{DEVISE}</span>
                            )}
                            {prixOk && (
                              <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 800 }}>
                                {fmt((+l.q || 0) * (+l.p || 0))}
                              </span>
                            )}
                            <button
                              onClick={() =>
                                setAvForm((p) => ({
                                  ...p,
                                  lignes: p.lignes.filter((x) => x.id !== l.id),
                                }))
                              }
                              style={{
                                background: 'none',
                                border: 'none',
                                color: C.red,
                                cursor: 'pointer',
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                      <div
                        onClick={() =>
                          setAvForm((p) => ({
                            ...p,
                            lignes: [...p.lignes, { id: uid(), d: '', q: 1, u: 'u', p: 0 }],
                          }))
                        }
                        style={{
                          textAlign: 'center',
                          border: '1px dashed ' + C.purple,
                          borderRadius: 8,
                          padding: 8,
                          fontSize: 10,
                          fontWeight: 700,
                          color: C.purple,
                          cursor: 'pointer',
                          marginBottom: 10,
                        }}
                      >
                        ＋ Ligne
                      </div>
                      {!prixOk && (
                        <div
                          style={{
                            background: C.purple + '12',
                            borderRadius: 8,
                            padding: '8px 11px',
                            marginBottom: 10,
                            fontSize: 11,
                            color: C.purple,
                            lineHeight: 1.5,
                          }}
                        >
                          {tr(
                            'Écris les travaux en plus, les quantités et les heures. Le bureau met les prix et l’envoie au client.',
                          )}
                        </div>
                      )}
                      {prixOk && (
                        <div
                          style={{
                            background: C.purple + '12',
                            borderRadius: 8,
                            padding: '8px 11px',
                            marginBottom: 10,
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <span style={{ fontSize: 10, fontWeight: 700, color: C.purple }}>
                            Montant de l'avenant
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 900, color: C.purple }}>
                            {DEVISE} {fmt(tot)}
                          </span>
                        </div>
                      )}
                      {prixOk && (
                        <>
                          <div
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              color: C.muted,
                              textTransform: 'uppercase',
                              marginBottom: 5,
                            }}
                          >
                            Accord client (signature)
                          </div>
                          <SignPad value={avSign} onChange={setAvSign} color={C.purple} />
                        </>
                      )}
                      <Btn
                        label="✓ Valider l'avenant"
                        // Le serveur exige un titre sur chaque ligne : une
                        // ligne sans designation faisait refuser tout le
                        // chantier, et l'avenant signe disparaissait. Le
                        // bouton reste actif, grise, et dit ce qui manque.
                        color={
                          !avForm.motif.trim() ||
                          (prixOk && (tot <= 0 || !avSign)) ||
                          rangsSansTitre(avForm.lignes).length > 0
                            ? '#94A3B8'
                            : C.purple
                        }
                        full
                        onClick={() => {
                          const manque = !avForm.motif.trim()
                            ? 'Indique d’abord le motif de l’avenant.'
                            : avForm.lignes.length === 0
                              ? 'Ajoute au moins une ligne avec « ＋ Ligne ».'
                              : messageTitreManquant(avForm.lignes, 'remplis sa « Désignation »') ||
                                (!prixOk
                                  ? null
                                  : tot <= 0
                                    ? 'Indique la quantité et le prix des travaux supplémentaires.'
                                    : !avSign
                                      ? 'Faites signer le client dans le cadre « Accord client ».'
                                      : null);
                          if (manque) {
                            alert(manque);
                            return;
                          }
                          const av = {
                            id: uid(),
                            motif: avForm.motif,
                            lignes: avForm.lignes.map((l) => ({
                              d: l.d,
                              q: +l.q || 0,
                              u: l.u,
                              p: +l.p || 0,
                            })),
                            montant: tot,
                            date: today(),
                            sig: avSign,
                          };
                          // Le montant du chantier est hors taxe et remise
                          // déduite, comme la facture finale qui reprendra
                          // l'avenant sous la remise de l'offre.
                          const offreCh = (devis || []).find((d) => d.id === ch.devisId);
                          const remiseCh = 1 - (+(offreCh?.remisePct ?? 0) || 0) / 100;
                          upd(selId, (c) => ({
                            ...c,
                            // Un avenant écrit sans les prix que le bureau
                            // chiffre : il garde sa place (et son identifiant).
                            avenants: avForm.editId
                              ? (c.avenants || []).map((x) =>
                                  x.id === avForm.editId
                                    ? {
                                        ...x,
                                        ...av,
                                        id: x.id,
                                        date: x.date || av.date,
                                        lignes: av.lignes.map((l, i) => ({
                                          ...l,
                                          id: avForm.lignes[i]?.id,
                                        })),
                                      }
                                    : x,
                                )
                              : [...(c.avenants || []), av],
                            facture: prixOk
                              ? Math.round(((c.facture || 0) + tot * remiseCh) * 100) / 100
                              : c.facture,
                          }));
                          setAvForm(null);
                          setAvSign('');
                        }}
                      />
                    </div>
                  </Card>
                );
              })()}
            {(ch.avenants || []).length === 0 && !avForm && (
              <div style={{ textAlign: 'center', padding: 30, color: C.muted, fontSize: 10 }}>
                Aucun avenant. Ajoute les travaux supplémentaires signés par le client.
              </div>
            )}
            {(ch.avenants || []).map((av) => (
              <Card key={av.id} style={{ marginBottom: 8 }}>
                <div style={{ padding: '11px 13px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 5,
                    }}
                  >
                    <div style={{ fontSize: 10, fontWeight: 700 }}>{av.motif}</div>
                    <div style={{ fontSize: 12, fontWeight: 900, color: C.purple }}>
                      {prixOk ? '+' + DEVISE + ' ' + fmt(av.montant) : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 8, color: C.muted }}>
                    {av.date} · {(av.lignes || []).length} ligne(s)
                    {av.sig ? ' · ✍ signé' : ' · ' + tr('à chiffrer par le bureau')}
                  </div>
                  {(av.lignes || []).map((l, i) => (
                    <div key={l.id ?? i} style={{ fontSize: 9.5, color: C.ink, marginTop: 2 }}>
                      {l.d} · {(+l.q || 0) + (l.u ? ' ' + l.u : '')}
                    </div>
                  ))}
                  {/* Écrit sans les prix par le technicien : le bureau met les
                      prix, fait signer le client, et le montant s'ajoute. */}
                  {prixOk && !av.sig && !(+av.montant > 0) && !avForm && (
                    <div style={{ marginTop: 8 }}>
                      <Btn
                        label={'💰 ' + tr('Mettre les prix')}
                        sm
                        color={C.purple}
                        onClick={() => {
                          setAvForm({
                            editId: av.id,
                            motif: av.motif || '',
                            lignes: (av.lignes || []).map((l) => ({ ...l, id: l.id ?? uid() })),
                          });
                          setAvSign('');
                        }}
                      />
                    </div>
                  )}
                </div>
              </Card>
            ))}
            <Card style={{ marginTop: 10 }}>
              <div style={{ padding: '6px 13px 12px' }}>
                <DocumentsJoints
                  genre="CHANTIER"
                  documentId={ch.id}
                  sortes={['Avenant signé']}
                  titre={tr('📎 AVENANTS SIGNÉS (PDF OU PHOTO)')}
                  filtre
                />
              </div>
            </Card>
          </div>
        )}

        {tab === 'plans' && (
          <div>
            <div style={{ marginBottom: 10 }}>
              <PhotoBtn
                label="📷 Ajouter un plan (photo / scan)"
                full
                cote={2600}
                color={C.teal}
                onPhoto={(apercu, fichier) => {
                  deposePlan(selId, fichier, { emoji: '📐' });
                }}
              />
              {/* Un plan reçu en PDF de l'architecte, ou l'image enregistrée
                  depuis son e-mail : il garde le nom de son fichier. Une
                  photo (HEIC de l'iPhone compris) part en JPEG, lisible sur
                  tous les téléphones. */}
              <label
                style={{
                  display: 'block',
                  marginTop: 6,
                  border: '1.5px dashed ' + C.teal,
                  color: C.teal,
                  borderRadius: 10,
                  padding: '10px',
                  fontSize: 12,
                  fontWeight: 800,
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                {tr('📎 Joindre un plan (PDF ou image)')}
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const original = e.target.files && e.target.files[0];
                    e.target.value = '';
                    if (!original) return;
                    const nomFichier = original.name || 'plan';
                    const estPdf =
                      original.type === 'application/pdf' || /\.pdf$/i.test(nomFichier);
                    const fichier = estPdf ? original : await reduitLImage(original, 2600);
                    deposePlan(selId, fichier, {
                      emoji: estPdf ? '📄' : '📐',
                      nom: nomFichier.replace(/\.[^.]+$/, '').slice(0, 120),
                      nomFichier: estPdf ? nomFichier : 'plan.jpg',
                    });
                  }}
                />
              </label>
            </div>
            {photoErreur && (
              <div
                role="alert"
                style={{
                  marginBottom: 10,
                  fontSize: 11,
                  color: C.red,
                  background: C.redL,
                  borderRadius: 8,
                  padding: '8px 11px',
                }}
              >
                {photoErreur}
              </div>
            )}
            {(ch.plans || []).length === 0 && (
              <div style={{ textAlign: 'center', padding: 30, color: C.muted, fontSize: 10 }}>
                Aucun plan. Les plans ajoutés ici sont visibles par les ouvriers du chantier ; «
                Envoyer au client » les lui envoie par e-mail.
              </div>
            )}
            {(ch.plans || []).map((pl) => (
              <Card key={pl.id} style={{ marginBottom: 8 }}>
                <div
                  style={{
                    padding: '11px 13px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 20 }}>{pl.emoji || '📄'}</span>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700 }}>{pl.nom}</div>
                      <div style={{ fontSize: 8, color: C.muted }}>
                        v{pl.version} · {pl.date} · {pl.taille}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {pl.src && (
                      <a
                        href={pl.src}
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: 'none' }}
                      >
                        <div
                          style={{
                            background: C.navy,
                            color: '#fff',
                            borderRadius: 8,
                            padding: '7px 11px',
                            fontSize: 9.5,
                            fontWeight: 700,
                          }}
                        >
                          👁 Voir
                        </div>
                      </a>
                    )}
                    <Btn
                      label="🗑"
                      sm
                      ghost
                      color={C.red}
                      disabled={typeof pl.id !== 'number'}
                      onClick={async () => {
                        if (!window.confirm(trf('Supprimer le plan « {0} » ?', pl.nom))) return;
                        setPhotoErreur('');
                        try {
                          await plansApi.supprime(selId, pl.id);
                          upd(selId, (c) => ({
                            ...c,
                            plans: (c.plans || []).filter((x) => x.id !== pl.id),
                          }));
                        } catch (e) {
                          setPhotoErreur(e.message || "Le plan n'a pas pu être supprimé.");
                        }
                      }}
                    />
                    <Btn
                      label={pl.envoye ? '✓ Envoyé' : '📤 Envoyer au client'}
                      sm
                      ghost={pl.envoye}
                      color={pl.envoye ? C.green : C.teal}
                      disabled={typeof pl.id !== 'number'}
                      onClick={async () => {
                        // Le bouton posait un drapeau sans rien envoyer. Le
                        // plan part au client par le serveur, fichier joint,
                        // avec sa ligne au journal des envois ; le drapeau ne
                        // se pose qu'une fois le message remis. Un plan sans
                        // fichier ou d'un format illisible est refusé, et le
                        // motif s'affiche.
                        setPhotoErreur('');
                        try {
                          await envoisApi.envoiePlan(selId, pl.id);
                          upd(selId, (c) => ({
                            ...c,
                            plans: (c.plans || []).map((x) =>
                              x.id === pl.id ? { ...x, envoye: true } : x,
                            ),
                          }));
                        } catch (e) {
                          setPhotoErreur(e.message || "Le plan n'a pas pu être envoyé.");
                        }
                      }}
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Shell>
    );
  }
  const ql = cQ.trim().toLowerCase();
  const liste = chantiers.filter((ch) => {
    const cl = clients.find((c) => c.id === ch.clientId);
    return (
      !ql ||
      String(ch.nom || '')
        .toLowerCase()
        .includes(ql) ||
      // L'identifiant est un nombre : `toLowerCase` y faisait tomber l'ecran.
      String(ch.code || '')
        .toLowerCase()
        .includes(ql) ||
      String(ch.id).toLowerCase().includes(ql) ||
      (cl?.nom || '').toLowerCase().includes(ql)
    );
  });
  // Un chantier terminé quitte la liste : il reste sous « Chantiers
  // terminés », et la recherche le retrouve toujours.
  const termines = liste.filter((ch) => ch.statut === 'Terminé');
  const enCours = liste.filter((ch) => ch.statut !== 'Terminé');
  const enVue = ql ? liste : voirTermines ? [...enCours, ...termines] : enCours;
  return (
    <Shell
      active="chantiers"
      onNav={onNav}
      soc={soc}
      pageTitle={'🏗️ ' + ((soc && soc.motChantier) || 'Chantiers')}
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <input
        value={cQ}
        onChange={(e) => setCQ(e.target.value)}
        placeholder="🔎 Rechercher (n°, nom, client)"
        style={inpFS}
      />
      {enVue.map((ch) => {
        const cl = clients.find((c) => c.id === ch.clientId);
        const docsCh = documentsDuChantier(ch, devis, factures);
        const achatsCh = (achatsDeLaSociete || [])
          .filter(
            (x) =>
              estUnAchat(x.piece) &&
              (x.genre === 'DEVIS'
                ? docsCh.devis.some((d) => d.id === x.documentId)
                : docsCh.factures.some((f) => f.id === x.documentId)),
          )
          .map((x) => x.piece);
        const { fac, st, mo, mat, ben } = chantierCosts(
          ch,
          employes,
          soc,
          factures,
          frais,
          achatsCh,
        );
        const factureCh = factureAuClient(ch, factures);
        const heuresCh = (ch.pointages || []).reduce(
          (s, p) => (p.type === 'absence' ? s : s + hours(p.entree, p.sortie, p.pause)),
          0,
        );
        // Les mêmes droits que la fiche : la direction voit tout, la
        // secrétaire les montants sans la main-d'œuvre ni le bénéfice, le
        // technicien aucun chiffre.
        const fermesListe = coutsFermes();
        const voitBenefListe =
          voitLeBenefice() && !fermesListe.heures && !fermesListe.sous && !fermesListe.facf;
        const suitListe = suitLesDepenses();
        const chiffres = casesConnues(
          voitBenefListe
            ? [
                ['Facturé HT', fmt(factureCh.ht)],
                ['Matériel', fmt(fac + mat)],
                ['Sous-trait.', fmt(st)],
                ['Heures', fmtH(heuresCh) + ' · ' + fmt(mo)],
                ['Bénéfice', fmt(ben), ben >= 0 ? C.green : C.red],
              ]
            : [
                ...(voitLesPrixDesOffres() && roleCan('factures')
                  ? [['Facturé HT', fmt(factureCh.ht)]]
                  : []),
                ['Matériel', fmt(fac + mat)],
                ['Sous-trait.', fmt(st)],
              ],
          fermesListe,
        );
        const msgNL = (ch.messages || []).filter((m) => m.de === 'ouvrier' && !m.lu).length;
        return (
          <Card
            key={ch.code || ch.id}
            style={{ marginBottom: 10, cursor: 'pointer' }}
            onClick={() => {
              setSelId(ch.id);
              setTab('resume');
            }}
          >
            <div style={{ padding: '13px 14px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 8,
                }}
              >
                <div style={{ flex: 1, marginRight: 10 }}>
                  <div style={{ fontSize: 8, color: C.muted }}>{ch.code || ch.id}</div>
                  <div style={{ fontSize: 11, fontWeight: 700 }}>{ch.nom}</div>
                  <div style={{ fontSize: 9, color: C.grey }}>{cl?.nom}</div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 5, flexWrap: 'wrap' }}>
                    {(ch.photos || []).length > 0 && (
                      <Pill label={'📷 ' + ch.photos.length} color={C.teal} size={7} />
                    )}
                    {(ch.sousTraitants || []).length > 0 && (
                      <Pill label={'🤝 ' + ch.sousTraitants.length} color={C.amber} size={7} />
                    )}
                    {msgNL > 0 && <Pill label={'💬 ' + msgNL} color={C.red} size={7} />}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Pill
                    label={ch.statut}
                    color={ch.statut === 'Terminé' ? C.green : C.amber}
                    size={7}
                  />
                  {suitListe && voitLesPrixDesOffres() && (
                    <>
                      <div style={{ fontSize: 7.5, color: C.muted, marginTop: 4 }}>
                        {ch.devisId ? 'Offre HT' : 'Montant HT'}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 900, color: C.navy }}>
                        {DEVISE} {fmt(ch.facture)}
                      </div>
                    </>
                  )}
                </div>
              </div>
              {suitListe && (
                <div
                  data-testid="chiffres-liste"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(' + chiffres.length + ',minmax(0,1fr))',
                    gap: 4,
                    marginBottom: 8,
                  }}
                >
                  {chiffres.map(([l, v, couleur]) => (
                    <div
                      key={l}
                      style={{
                        background: C.bg,
                        borderRadius: 7,
                        padding: '6px 3px',
                        textAlign: 'center',
                        minWidth: 0,
                      }}
                    >
                      <div style={{ fontSize: 6.5, color: C.muted }}>{l}</div>
                      <div
                        style={{
                          fontSize: 8,
                          fontWeight: 800,
                          color: couleur || C.ink,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {v}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ background: C.border, borderRadius: 99, height: 5 }}>
                <div
                  style={{
                    width: avancementDuChantier(ch) + '%',
                    height: 5,
                    borderRadius: 99,
                    background: C.navy,
                  }}
                />
              </div>
            </div>
          </Card>
        );
      })}
      {enVue.length === 0 && (
        <Card>
          <div style={{ padding: 20, textAlign: 'center', color: C.muted, fontSize: 11 }}>
            {tr('Aucun chantier')}
          </div>
        </Card>
      )}
      {!ql && termines.length > 0 && (
        <button
          type="button"
          aria-expanded={voirTermines}
          onClick={() => setVoirTermines((v) => !v)}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            gap: 6,
            padding: '11px 0',
            marginTop: 4,
            borderRadius: 12,
            border: '1px solid ' + C.border,
            background: C.white,
            color: C.green,
            fontSize: 11.5,
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          <span>
            {voirTermines ? tr('Masquer') : '✓ ' + tr('Terminé') + ' (' + termines.length + ')'}
          </span>
          <span aria-hidden="true">{voirTermines ? '▴' : '▾'}</span>
        </button>
      )}
      <div style={{ fontSize: 8.5, color: C.muted, textAlign: 'center', marginTop: 8 }}>
        {termesOffre(soc, { estMag: false }).feminin
          ? 'Un chantier est créé automatiquement quand une offre est acceptée.'
          : 'Un chantier est créé automatiquement quand un devis est accepté.'}
      </div>
    </Shell>
  );
}
